import UnassignedBucket from "../../drawings/UnassignedBucket";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FieldShopLinks, { PurposeChip } from "../../drawings/FieldShopLinks";
import { openDrawing } from "../../drawings/openDrawing";
import { createPortal } from "react-dom";
import { Camera, ChevronDown, ChevronRight, Check, X, MapPin, ChevronsLeft, ChevronsRight, Download, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import ScopePills from "./ScopePills";
import GroupCountDialog from "./GroupCountDialog";
import { levelVisibility, scopeCrumb } from "./HierarchyContext";
import LocationCell, { locKey, roomKey3 } from "./LocationCell";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// A grid column and a raw FRS row hold the same product under different key
// names — normalise once so every helper below reads one shape.
const productOf = (input) => (input?.desc !== undefined ? {
  part: input.part || "", code: input.code || "", short: input.short || "",
  model: input.model || "", keynote: input.keynote || "", desc: input.dtext || "",
  full: input.desc || "", maker: input.maker || "", color: input.color || "",
  size: input.size || "", finish: input.finish || "", uom: input.uom || "",
  scope: input.scope || "",
} : {
  part: input?.product_part_number || "", code: input?.product_code || "",
  short: input?.product_short || "", model: input?.product_model || "",
  keynote: input?.product_keynote || "", desc: input?.product_desc || "",
  full: input?.product_description || "", maker: input?.manufacturer || "",
  color: input?.product_color || "", size: input?.product_size || "",
  finish: input?.product_finish || "", uom: input?.unit_of_measure || "",
  scope: input?.scope_type || "",
});

// FRS files pack everything into one string: "PART# — Description — Maker".
// Stored fields always win; this only covers rows imported before they existed.
const splitFull = (full) => {
  const segs = String(full || "").split(/\s*[—–]\s*/).map((s) => s.trim()).filter(Boolean);
  // "Description — Maker" (no part number up front): the first segment reads as words, not a code.
  if (segs.length === 2 && !/^\S+$/.test(segs[0]) && segs[0].length > 12) {
    return { prefix: "", middle: segs[0], tail: segs[1] };
  }
  return segs.length > 1
    ? { prefix: segs[0], middle: segs[1], tail: segs.slice(2).join(" — ") }
    : { prefix: "", middle: segs[0] || "", tail: "" };
};

export const productFields = (input) => {
  const p = productOf(input);
  const s = splitFull(p.full);
  return {
    ...p,
    part: p.part || s.prefix || p.code || p.short || "",
    desc: p.desc || s.middle || "",
    keynote: p.keynote || p.code || "",
    maker: p.maker || s.tail || "",
  };
};

// P2-1 — every product header reads Part # · Description · Color/Finish (no per-column choice).
export const headerLines = (col) => {
  const f = productFields(col);
  const tip = [f.part, f.short, f.desc, f.color, f.finish, f.maker].filter(Boolean).join("\n");
  const lines = [];
  if (f.part) lines.push({ text: f.part, primary: true });
  if (f.desc) lines.push({ text: f.desc, primary: !lines.length });
  const cf = f.color || f.finish;
  if (cf) lines.push({ text: cf, primary: false });
  if (!lines.length) lines.push({ text: f.short || "—", primary: true });
  return { lines, tip, field: "auto" };
};

// P2-2 — a product column takes the tint of the colour word in its Color, Finish or Description.
const TINTS = [
  [/\b(blue|navy|azure|teal)\b/, "Blue", "14,165,233"], [/\b(gr[ae]y|silver|slate|charcoal|pewter)\b/, "Gray", "100,116,139"],
  [/\b(green|olive|sage)\b/, "Green", "16,185,129"], [/\b(red|maroon|burgundy|crimson)\b/, "Red", "239,68,68"],
  [/\b(tan|beige|almond|sand|khaki|bisque)\b/, "Tan", "180,140,90"], [/\b(black|ebony)\b/, "Black", "51,65,85"],
  [/\b(white|ivory|cream)\b/, "White", "203,213,225"], [/\b(brown|bronze|walnut|espresso)\b/, "Brown", "146,94,50"],
  [/\b(yellow|gold)\b/, "Yellow", "234,179,8"], [/\b(orange)\b/, "Orange", "249,115,22"],
  [/\b(purple|violet|plum)\b/, "Purple", "139,92,246"], [/\b(pink|rose)\b/, "Pink", "236,72,153"],
];
const tintOf = ([, word, rgb], source) => ({ word, source, head: `rgba(${rgb},0.28)`, cell: `rgba(${rgb},0.08)` });
export const TINT_WORDS = TINTS.map((t) => [t[1], `rgb(${t[2]})`]);
export const tintFor = (col) => {
  if (col?.tint === "none") return null;
  const manual = col?.tint && TINTS.find((t) => t[1] === col.tint);
  if (manual) return tintOf(manual, "Manual");
  const f = productFields(col);
  for (const [src, text] of [["Color", f.color], ["Finish", f.finish], ["Description", f.desc], ["FRS text", f.full]]) {
    const t = String(text || "").toLowerCase();
    if (!t) continue;
    const hit = TINTS.find(([re]) => re.test(t));
    if (hit) return tintOf(hit, src);
  }
  return null;
};

// Two-line product label for the tree view and the request dialog summaries.
export const headerLabels = (input) => {
  const f = productFields(input);
  const part = f.part || f.short || "—";
  return {
    part,
    short: f.short && f.short.toUpperCase() !== part.toUpperCase() ? f.short : "",
    description: f.desc,
    tip: [f.part, f.short, f.desc, f.color, f.maker].filter(Boolean).join("\n"),
  };
};


// P2-6 — the FRS strip reads as three groups, left to right: Filter · Group · Expand.
const Cluster = ({ label, children, testId, compact }) => (
  <span className="inline-flex flex-col gap-0.5" data-testid={testId}>
    {!compact && <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 leading-none">{label}</span>}
    <span className="inline-flex flex-wrap items-center gap-1">{children}</span>
  </span>
);
const Divider = () => <span className="h-7 w-px self-end bg-gray-200 dark:bg-gray-700" aria-hidden="true" />;

// ---- Helpers ----
export const roomKey = (it) =>
  `${it.building || ""}||${it.floor || ""}||${it.area || ""}||${it.room_number || ""}||${it.room_type || ""}`;
export const colKey = (it) =>
  `${it.scope_type || ""}||${it.product_code || ""}||${it.product_description || ""}`;

const natCmp = (a, b) => {
  const na = parseInt(String(a).replace(/\D+/g, ""), 10);
  const nb = parseInt(String(b).replace(/\D+/g, ""), 10);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
  return String(a).localeCompare(String(b));
};

// Candidate FRS meta columns in the exact order they appear on the FRS
// spreadsheet. Only columns with at least one non-empty value across the
// uploaded items are rendered — blank FRS columns are skipped entirely.
const META_DEFS = [
  { field: "room_name",       label: "Room Name",           w: 150 },
  { field: "room_number",     label: "Room #",              w: 72 },
  { field: "building",        label: "Building",            w: 110 },
  { field: "floor",           label: "Floor",               w: 52 },
  { field: "area",            label: "Area",                w: 84 },
  { field: "room_type",       label: "Room Type",           w: 120 },
  { field: "floor_plan",      label: "Floor Plan",          w: 90 },
  { field: "plan_sheet",      label: "Enlarged Floor Plan", w: 104 },
  { field: "elevation_sheet", label: "Elevation",           w: 80 },
  { field: "num_rooms",       label: "# of Rooms",          w: 72, numeric: true },
];
// Locked to the checkbox / readiness dot itself plus a hair of breathing room —
// these two columns never borrow width from the product columns.
const CHECK_W_BASE = 24;
const READY_W_BASE = 24;

// Derived readiness for a set of items: green when every item is ready,
// red when none are, amber when mixed. Room-level `readiness_status` is the
// only stored state — this is purely a rollup.
const readinessOf = (its) => {
  const total = its.length;
  if (!total) return { state: "not_ready", ready: 0, total: 0 };
  const ready = its.filter((i) => ["ready", "scheduled", "completed"].includes(i.readiness_status)).length;
  return { state: ready === total ? "ready" : ready === 0 ? "not_ready" : "partial", ready, total };
};
const READY_DOT = { ready: "#16a34a", partial: "#f59e0b", not_ready: "#dc2626" };
const READY_LABEL = { ready: "Ready", partial: "Partially ready", not_ready: "Not Ready" };

const hasVal = (v) => v !== undefined && v !== null && String(v).trim() !== "";
// Administrator-defined room columns live in `custom_fields`, so a meta def
// reads either a plain FRS field or one of those keys.
const valOf = (it, d) => (d.custom ? (it.custom_fields || {})[d.custom] : it[d.field]);

// ---- Cluster 8A completion rollups ----
// completions is keyed `${roomKey}||${scope}`. A room's percent is the
// A row added to the FRS after the room was signed off was not part of that
// sign-off, so it counts as zero installed (mirrors installed_units() on the
// backend).
export const rowInstalled = (row, comp) => {
  const rq = Number(row.quantity || 0);
  if (!comp) return 0;
  const born = String(row.created_at || "");
  const compAt = String(comp.updated_at || comp.created_at || "");
  if (born && compAt && born > compAt) return 0;
  if (Number(comp.percent_complete || 0) >= 1) return rq;
  const remMap = comp.remaining_units_by_item || {};
  if (remMap[row.id] !== undefined) return Math.max(0, rq - Number(remMap[row.id] || 0));
  return (comp.remaining_item_ids || []).includes(row.id) ? 0 : rq;
};

// units-weighted average of its scopes; group rows weight across their rooms.
const pctFor = (roomItems, completions) => {
  let done = 0;
  let total = 0;
  const byCell = new Map();   // `${roomKey}||${scope}` -> { units, rows }
  for (const it of roomItems) {
    const k = `${roomKey(it)}||${it.scope_type || ""}`;
    const cell = byCell.get(k) || { units: 0, rows: [] };
    cell.units += Number(it.quantity || 0);
    cell.rows.push(it);
    byCell.set(k, cell);
  }
  for (const [k, cell] of byCell.entries()) {
    if (cell.units <= 0) continue;
    total += cell.units;
    const comp = completions[k];
    done += cell.rows.reduce((sum, row) => sum + rowInstalled(row, comp), 0);
  }
  return { pct: total > 0 ? done / total : 0, units: total };
};

// P2-4 — hours progress: "<actual> of <budget>h" · pct, thin bar; amber from 90 %, red past 100 %.
const HoursCell = ({ actual, budget, testid, bg, done }) => {
  const pct = budget > 0 ? Math.round((actual / budget) * 100) : 0;
  const tone = pct > 100 ? "red" : pct >= 90 ? "amber" : "emerald";
  const fmt = (n) => (Math.round(n * 10) / 10).toLocaleString();
  return (
    <td data-testid={testid} data-pct={pct} className="px-2 py-1 border-l border-gray-200 dark:border-gray-700 whitespace-nowrap tabular-nums"
        style={{ minWidth: 104, ...(bg ? { background: bg } : {}) }}
        title={budget > 0 ? `${fmt(actual)} of ${fmt(budget)} budgeted hours (${pct}%)` : "No hours budget for this row"}>
      {budget > 0 || actual > 0 ? (
        <>
          <div className={`flex items-center justify-between gap-2 text-[11px] ${tone === "red" ? "text-red-700 dark:text-red-400 font-semibold" : "text-gray-800 dark:text-gray-200"}`}>
            <span>{fmt(actual)} of {fmt(budget)}h{done && actual === 0 ? <span className="ml-1 text-emerald-700 dark:text-emerald-400" title="Installed — no time logged against this room">· done</span> : null}</span>
            <span className="font-semibold">{budget > 0 ? `${pct}%` : "—"}</span>
          </div>
          <div className="mt-0.5 h-1 w-full rounded bg-gray-200 dark:bg-gray-700">
            <div className={`h-1 rounded ${tone === "red" ? "bg-red-500" : tone === "amber" ? "bg-amber-500" : "bg-emerald-500"}`}
                 style={{ width: `${Math.min(100, pct)}%` }} />
          </div>
        </>
      ) : <span className="text-[11px] text-gray-400">—</span>}
    </td>
  );
};

const PctCell = ({ pct: raw, units, testid, bg, hours }) => {
  if (hours) return <HoursCell {...hours} done={(raw || 0) >= 1 && units > 0} testid={testid} bg={bg} />;
  const pct = Math.round((raw || 0) * 100);
  const full = pct >= 100 && units > 0;
  return (
    <td
      data-testid={testid}
      data-pct={pct}
      className={`px-2 py-1 text-right tabular-nums whitespace-nowrap border-l border-gray-200 ${full ? "text-emerald-700 font-bold" : pct > 0 ? "text-gray-800 font-semibold" : "text-gray-500"}`}
      style={{ minWidth: 36, ...(bg ? { background: bg } : {}) }}
    >
      {units > 0 ? (full ? <span className="inline-flex items-center gap-1">100%<Check className="w-3.5 h-3.5" /></span> : `${pct}%`) : ""}
    </td>
  );
};

const readinessBg = (status) => {
  if (status === "ready") return "var(--cell-ready-bg)";
  if (status === "scheduled") return "var(--cell-sched-bg)";
  if (status === "completed") return "var(--cell-done-bg)";
  return "";
};

const stickyStyle = (left, width, bg = "var(--sticky-cell-bg)", zIndex = 5) => ({
  position: "sticky",
  left: `${left}px`,
  minWidth: `${width}px`,
  maxWidth: `${width}px`,
  background: bg,
  zIndex,
});

/**
 * Pivoted FRS grid view:
 *   rows = rooms (grouped by Building > Floor, collapsible)
 *   left frozen columns = DYNAMIC — mirrors exactly the FRS columns that
 *     contain data in this upload (blank FRS columns are skipped)
 *   columns = products (grouped by scope_type header band)
 *   cells = qty (colored by readiness_status)
 * Selection is per-room (matches the tree's selection semantics so
 * Mark Ready / Unmark / Request work identically downstream).
 */
const PLAN_FIELDS = new Set(["floor_plan", "plan_sheet", "elevation_sheet"]);

export default function FrsGridView({ items, selected, setSelected, onSetReadiness, renderDateCells,
                                     emptyState = null,
                                     completions = {}, onCellAction, installMode = false, toolbarSlot = null, exportRef = null,
                                     expandSignal = 0, punchCounts = {}, renderGroupDateCells = null,
                                     hoursByRoom = null, progressMode = "units", onProgressMode = null, onPunchRoom = null, onSetTint = null,
                                     roomColumns = [], headerConfig = {}, onHeaderConfig = null,
                                     onEditProduct = null,
                                     // FLD-1 pulling mode — same grid, different columns and a
                                     // selection tree down to the product.
                                     pickingMode = false, dateLabels = ["Start", "ABS Comp", "Completion"],
                                     scopeOptions = null, scopeFilter = null, onScopeFilter = null, hideUnassigned = false, onUnassignedChanged = null,
                                     datesCollapsed = false, onToggleDates = null,
                                     onHand = {}, cellState = { lines: {}, on_hand: {} },
                                     onChaseTicket = null,
                                     pickSel = {}, onPickCells = null,
                                     onPickProduct = null,
                                     // FLD-4 material mode — the same grid, but every product
                                     // cell shows what is ON SITE AND UNINSTALLED instead of
                                     // what the FRS demands. `onSite` is keyed by frs_item_key.
                                     // FT-3a — edit mode. The pending-edit state lives in FrsTable;
                                     // the grid only renders what these hand back, so the matrix
                                     // maths is untouched.
                                     // PLAN-COMPLETE §5 — item_id → units installed once the
                                     // pending change from the plan is saved. The cell renders
                                     // from this instead of the saved completion, so a tapped
                                     // change is highlighted where it will land.
                                     pendingItems = null,
                                     editCell = null, editMetaCell = null, changedKeys = null,
                                     // FT-3a parity — a per-room action button in the ROOM # cell
                                     // (room information, duplicate, delete) and "remove from the
                                     // whole job" on a product. Both only in edit mode.
                                     editRowAction = null, onRemoveProductJob = null,
                                     // FT-3 — group the rows by location (Building › Floor › Area) or by
                                     // room type; a collapsed header cell in edit mode opens the bulk edit;
                                     // the view exports exactly as displayed.
                                     groupBy = "location", onGroupBy = null, onBulkCell = null,
                                     onExportView = null,
                                     materialMode = false, onSite = {}, onLocationCell = null,
                                     // DRW — FRS → plan: which sheet numbers / rooms have a drawing
                                     planLinks = null, onOpenPlan = null, shopLinks = null, projectId = "", linkFrom = "install",
                                     // DRW-LINKS — collapse state lifted out so a drawing link can
                                     // come home to the same open branches
                                     collapsedInit = null, onCollapsedChange = null,
                                     // FLD-3 Delivery — only records still To Account For can be ticked
                                     selectableIds = null, qtyLabel = "",
                                     // FLD-3 Delivery — the ticket's own numbers per FRS record.
                                     // Keyed by frs_item_key: { released, placed, not_delivered, unaccounted }.
                                     // Delivery material has NOT arrived yet by definition, so these
                                     // render regardless of what is on site.
                                     deliveryMap = null,
                                     // FLD-PHOTO — counts behind the camera chips, and the openers
                                     photoCounts = null, onLocationPhotos = null, onInstallPhotos = null }) {
  const navigate = useNavigate();
  const showChecks = !installMode || materialMode;    // FLD-13 — bulk room move needs the checkboxes
  const pickable = (ids) => (selectableIds ? ids.filter((id) => selectableIds.has(id)) : ids);
  const [collapsed, setCollapsed] = useState(new Set());
  const [countFor, setCountFor] = useState(null);   // { label, rooms } — quick count of a collapsed level
  // Which product column's header-field menu is open, and where to draw it.
  // Cluster 9 — a dispatch-focused view opens fully expanded.
  useEffect(() => { if (expandSignal > 0) setCollapsed(new Set()); }, [expandSignal]);

  // Install on a phone held vertically: ROOM # leads and is the only frozen column, ROOM NAME
  // scrolls away with the products, and the MOVE / READINESS columns get real width so the dot
  // no longer sits on top of the room number. Landscape (and every other view) is untouched.
  const [phone, setPhone] = useState(
    () => (typeof window === "undefined"
      ? { narrow: false, portrait: false }
      : { narrow: window.innerWidth < 640, portrait: window.innerHeight >= window.innerWidth }),
  );
  useEffect(() => {
    const read = () => setPhone({
      narrow: window.innerWidth < 640,
      portrait: window.innerHeight >= window.innerWidth,
    });
    read();
    window.addEventListener("resize", read);
    const mq = window.matchMedia?.("(orientation: portrait)");
    mq?.addEventListener?.("change", read);
    return () => {
      window.removeEventListener("resize", read);
      mq?.removeEventListener?.("change", read);
    };
  }, []);
  const phonePortrait = phone.narrow && phone.portrait;
  // On a phone the released / placed / not-delivered / ready note wrapped onto four lines and ate
  // the matrix. Every view keeps just the room count on the building, floor and area rows.
  const brief = phone.narrow;

  // Which FRS meta columns actually carry data in this upload.
  // The field view drops the Request checkbox and Readiness dot columns so the
  // quantity cells stay reachable on a phone.
  const CHECK_W = installMode && !phonePortrait ? 0 : CHECK_W_BASE;
  // The field view drops both the Request checkbox and the readiness dot —
  // every pixel of width belongs to the quantity columns on a phone.
  const READY_W = installMode && !phonePortrait ? 0 : READY_W_BASE;

  const [productInfo, setProductInfo] = useState(null);
  useEffect(() => {
    if (!productInfo) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setProductInfo(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [productInfo]);

  // Built-in FRS meta columns plus any room column an administrator added.
  const allDefs = useMemo(() => [
    ...META_DEFS,
    ...roomColumns.map((c) => ({ field: `custom:${c.key}`, custom: c.key, label: c.label, w: 100 })),
  ], [roomColumns]);

  const metaCols = useMemo(() => {
    // Install (field) mode pins only the columns a phone screen needs.
    const INSTALL_FIELDS = ["room_number", "room_name"];
    // Building and Floor live in the group header above the rows — repeating
    // them per row wasted width the product columns need (Cluster 12).
    const HEADER_FIELDS = ["building", "floor"];
    // Pulling hides the sheet references — the foreman is pulling material,
    // not reading drawings.
    const PICK_HIDDEN = ["floor_plan", "plan_sheet", "elevation_sheet"];
    // DRW-B §14 — plan pages live behind the room number / name link everywhere (RA matches
    // Install): the sheet columns are gone from the grid, the room link opens them.
    const pool = (installMode ? allDefs.filter((d) => INSTALL_FIELDS.includes(d.field)) : allDefs)
      .filter((d) => !HEADER_FIELDS.includes(d.field))
      .filter((d) => !PLAN_FIELDS.has(d.field) && !(pickingMode && PICK_HIDDEN.includes(d.field)));
    const active = pool.filter((d) => items.some((it) => hasVal(valOf(it, d))));
    // Phone portrait, Material only: ROOM # leads so the one frozen column is the room number.
    const ordered = phonePortrait
      ? [...active].sort((a, b) => (a.field === "room_number" ? -1 : 0) - (b.field === "room_number" ? -1 : 0))
      : active;
    // Content-driven width. Rotated headers only need ~24px, so the column
    // width is driven by the widest visible cell value (capped at 11 chars).
    // ≈ 7.5 px per char + 18 px padding. Values longer than 11 chars will be
    // truncated with an ellipsis and shown in a hover tooltip.
    let left = (installMode && !phonePortrait ? 0 : CHECK_W_BASE + READY_W_BASE);
    return ordered.map((d) => {
      let maxLen = 3; // floor so single-char columns aren't razor thin
      for (const it of items) {
        const v = valOf(it, d);
        if (v === undefined || v === null) continue;
        const s = String(v);
        if (s.length > maxLen) maxLen = s.length;
      }
      const capped = Math.min(11, maxLen);
      // Field mode carries the room's plan references under the room name, so
      // that column needs the extra room.
      const w = phonePortrait && d.field === "room_number"
        // The frozen column on a phone holds the room number with a little breathing room.
        ? Math.max(56, Math.round(capped * 8) + 20)
        : installMode && d.field === "room_name"
        ? 120
        : Math.max(42, Math.round(capped * 7.5) + 18);
      const col = { ...d, w, left };
      left += w;
      return col;
    });
  }, [items, installMode, allDefs, pickingMode, phonePortrait]);
  const frozenEnd = CHECK_W + READY_W + metaCols.reduce((s, c) => s + c.w, 0);
  // Field mode hides the schedule dates entirely — they stay on the Project tab.
  const dateCols = installMode ? 0 : dateLabels.length;
  // Plan references the installer needs, shown per room only when the FRS
  // actually carries a value for that room.
  const chipField = metaCols.some((c) => c.field === "room_name") ? "room_name" : "room_number";
  // Area is already a level of the tree above the room — the chip would only repeat it.
  const roomRefs = (r) => [
    ["Floor Plan", r.floor_plan],
    ["Enlarged Floor Plan", r.plan_sheet], ["Elevations", r.elevation_sheet],
    ...roomColumns.map((c) => [c.label, r[`custom:${c.key}`]]),
  ].filter(([, v]) => hasVal(v));

  const { rooms, columns, qtyMap, readyQtyByRoom, idsByRoom, itemsByRoom, itemByRC, itemsByRC, colGroups, totalsByCol, grandTotal, grandReady } = useMemo(() => {
    const roomsByKey = new Map();
    const colsByKey = new Map();
    const qtyMap = {};
    const readyQtyByRoom = {};
    const idsByRoom = {};
    const itemsByRoom = {};
    const itemByRC = {};
    const itemsByRC = {};
    for (const it of items) {
      const rk = roomKey(it);
      const ck = colKey(it);
      if (!roomsByKey.has(rk)) {
        const meta = { key: rk };
        for (const d of allDefs) meta[d.field] = hasVal(valOf(it, d)) ? String(valOf(it, d)) : "";
        meta._scopes = new Set();
        roomsByKey.set(rk, meta);
      } else {
        // Backfill any meta field a later product-row carries but the first didn't.
        const meta = roomsByKey.get(rk);
        for (const d of allDefs) {
          if (!meta[d.field] && hasVal(valOf(it, d))) meta[d.field] = String(valOf(it, d));
        }
      }
      if (!colsByKey.has(ck)) {
        colsByKey.set(ck, {
          key: ck,
          scope: it.scope_type || "",
          scopeCode: it.scope_code || "",
          code: it.product_code || "",
          desc: it.product_description || "",
          short: it.product_short || "",
          model: it.product_model || "",
          part: it.product_part_number || "",
          keynote: it.product_keynote || "",
          dtext: it.product_desc || "",
          color: it.product_color || "",
          maker: it.manufacturer || "",
          size: it.product_size || "",
          finish: it.product_finish || "",
          uom: it.unit_of_measure || "",
          uid: it.product_uid || "",
          tint: it.header_tint || "",
        });
      }
      roomsByKey.get(rk)._scopes.add(it.scope_code || it.scope_type || "");
      qtyMap[rk] = qtyMap[rk] || {};
      // A change-order delta row keeps the parent's dispatched quantity intact and carries the
      // difference in `delta_qty`. Summing only `quantity` hid it, so a cell read one unit light
      // the moment a mob-locked row was changed.
      qtyMap[rk][ck] = (qtyMap[rk][ck] || 0)
        + (it.is_delta ? Number(it.delta_qty || 0) : Number(it.quantity || 0));
      idsByRoom[rk] = idsByRoom[rk] || [];
      idsByRoom[rk].push(it.id);
      itemsByRoom[rk] = itemsByRoom[rk] || [];
      itemsByRoom[rk].push(it);
      itemByRC[`${rk}|${ck}`] = it;
      // A room can hold several FRS rows for one product — keep them all so a
      // cell's progress is the SUM of its rows, not the first row's state.
      itemsByRC[`${rk}|${ck}`] = itemsByRC[`${rk}|${ck}`] || [];
      itemsByRC[`${rk}|${ck}`].push(it);
      if (["ready", "scheduled", "completed"].includes(it.readiness_status)) {
        readyQtyByRoom[rk] = (readyQtyByRoom[rk] || 0)
          + (it.is_delta ? Number(it.delta_qty || 0) : Number(it.quantity || 0));
      }
    }
    const byType = groupBy === "room_type";
    for (const r of roomsByKey.values()) r._scope = [...r._scopes].filter(Boolean).sort(natCmp).join(" + ");
    const rooms = [...roomsByKey.values()].sort(
      (a, b) =>
        (byType ? natCmp(a.room_type || "\uffff", b.room_type || "\uffff") : 0) ||
        (groupBy === "scope" ? natCmp(a._scope, b._scope) : 0) ||
        natCmp(a.building, b.building) ||
        natCmp(a.floor, b.floor) ||
        natCmp(a.room_number, b.room_number),
    );
    const columns = [...colsByKey.values()].sort(
      (a, b) => natCmp(a.scope, b.scope) || natCmp(a.code, b.code) || natCmp(a.desc, b.desc),
    );
    const colGroups = [];
    for (const c of columns) {
      const last = colGroups[colGroups.length - 1];
      if (last && last.scope === c.scope) last.count++;
      else colGroups.push({ scope: c.scope, scopeCode: c.scopeCode, count: 1 });
    }
    const totalsByCol = {};
    columns.forEach((c) => { totalsByCol[c.key] = 0; });
    let grandTotal = 0;
    let grandReady = 0;
    rooms.forEach((r) => {
      Object.entries(qtyMap[r.key] || {}).forEach(([ck, q]) => {
        totalsByCol[ck] = (totalsByCol[ck] || 0) + q;
        grandTotal += q;
      });
      grandReady += (readyQtyByRoom[r.key] || 0);
    });
    return { rooms, columns, qtyMap, readyQtyByRoom, idsByRoom, itemsByRoom, itemByRC, itemsByRC, colGroups, totalsByCol, grandTotal, grandReady };
  }, [items, allDefs, groupBy]);
  const tintByCol = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, tintFor(c)])), [columns]);

  // The header band is a fixed height in every view — the installer view's
  // height, so RA and the field see exactly the same grid. A value too long
  // for it is truncated with an ellipsis and shown in full on hover.
  const HEAD_H = 96;

  // FLD-4 — the Material view's numbers. Same shape as qtyMap / totalsByCol /
  // grandTotal so every rollup below is the identical arithmetic, just fed on
  // site quantities instead of demand.
  const { siteMap, siteTotalsByCol, siteGrand, siteSurplusByRoom, locByRoom, notOnHandByRoom,
          returnedByRoom } =
    useMemo(() => {
      const siteMap = {};
      const siteTotalsByCol = {};
      const siteSurplusByRoom = {};
      const locByRoom = {};
      const notOnHandByRoom = {};
      const returnedByRoom = {};
      let siteGrand = 0;
      for (const it of items) {
        const rk = roomKey(it);
        const ck = colKey(it);
        const s = onSite[it.frs_item_key] || {};
        const q = Number(s.on_site || 0);
        siteMap[rk] = siteMap[rk] || {};
        siteMap[rk][ck] = (siteMap[rk][ck] || 0) + q;
        siteTotalsByCol[ck] = (siteTotalsByCol[ck] || 0) + q;
        siteGrand += q;
        siteSurplusByRoom[rk] = (siteSurplusByRoom[rk] || 0) + Number(s.surplus || 0);
        if (q > 0) {
          const set = locByRoom[rk] || new Set();
          (s.locations || []).forEach((x) => { if (x) set.add(x); });
          locByRoom[rk] = set;
        }
        // pulling sheet: where the delivery was dropped, straight from the placements
        if (pickingMode) {
          const l = (cellState.lines || {})[it.frs_item_key] || {};
          if ((l.locations || []).length) {
            const set = locByRoom[rk] || new Set();
            l.locations.forEach((x) => { if (x) set.add(x); });
            locByRoom[rk] = set;
          }
        }
        if (s.not_on_hand) {
          (notOnHandByRoom[rk] = notOnHandByRoom[rk] || []).push(s.not_on_hand);
        }
        // FLD-14 — material that went out and did not stay: returning to the shop, or open again
        // because the ticket closed without it. The room needs it either way.
        const cl = (cellState.lines || {})[it.frs_item_key] || {};
        if (cl.returning > 0 || cl.reopened > 0) {
          (returnedByRoom[rk] = returnedByRoom[rk] || []).push({
            product_short: it.product_short || it.product_code || "",
            returning: Number(cl.returning || 0), reopened: Number(cl.reopened || 0),
            not_delivered: Number(cl.not_delivered || 0),
            tickets: [...(cl.returning_tickets || []), ...(cl.reopened_tickets || [])],
            reasons: cl.reasons || [],
          });
        }
      }
      return { siteMap, siteTotalsByCol, siteGrand, siteSurplusByRoom, locByRoom, notOnHandByRoom,
               returnedByRoom };
    }, [items, onSite, pickingMode, cellState]);

  // FLD-3 Delivery — the ticket's numbers, in the same shape as qtyMap / totalsByCol / grandTotal
  // so every rollup below is the identical arithmetic. Each FRS record is counted once (keyed by
  // frs_item_key), so a parent total is exactly the sum of the delivery records beneath it.
  const DEL = !!deliveryMap;
  const DEL_FIELDS = ["released", "placed", "not_delivered", "unaccounted"];
  const DEL_LABELS = { released: "Released", placed: "Placed", not_delivered: "Not Delivered", unaccounted: "To Account For" };
  const { delMap, delTotalsByCol, delGrand, delByRoom, delCellByRC } = useMemo(() => {
    const delMap = {};
    const delTotalsByCol = {};
    const delByRoom = {};
    const delCellByRC = {};
    let delGrand = 0;
    if (!deliveryMap) return { delMap, delTotalsByCol, delGrand, delByRoom, delCellByRC };
    const seen = new Set();
    for (const it of items) {
      const key = it.frs_item_key || "";
      if (seen.has(key)) continue;          // one delivery record per FRS key — never counted twice
      const rec = deliveryMap[key];
      if (!rec) continue;
      seen.add(key);
      const rk = roomKey(it);
      const ck = colKey(it);
      const rel = Number(rec.released || 0);
      delMap[rk] = delMap[rk] || {};
      delMap[rk][ck] = (delMap[rk][ck] || 0) + rel;
      delTotalsByCol[ck] = (delTotalsByCol[ck] || 0) + rel;
      delGrand += rel;
      const bucket = delByRoom[rk] = delByRoom[rk] || { released: 0, placed: 0, not_delivered: 0, unaccounted: 0 };
      const cell = delCellByRC[`${rk}|${ck}`] = delCellByRC[`${rk}|${ck}`] || { released: 0, placed: 0, not_delivered: 0, unaccounted: 0 };
      for (const f of DEL_FIELDS) {
        bucket[f] += Number(rec[f] || 0);
        cell[f] += Number(rec[f] || 0);
      }
    }
    return { delMap, delTotalsByCol, delGrand, delByRoom, delCellByRC };
  }, [items, deliveryMap]);   // eslint-disable-line react-hooks/exhaustive-deps
  const delSum = (rs, field) => rs.reduce((s, r) => s + Number((delByRoom[r.key] || {})[field] || 0), 0);

  // One switch, and every rollup in the table below follows.
  const QMAP = DEL ? delMap : materialMode ? siteMap : qtyMap;
  const TOTALS = DEL ? delTotalsByCol : materialMode ? siteTotalsByCol : totalsByCol;
  const GRAND = DEL ? delGrand : materialMode ? siteGrand : grandTotal;
  const QTY_COLS = DEL ? DEL_FIELDS.length : 1;
  const LOC_COLS = materialMode ? 1 : 0;

  // Units-weighted completion rollup for a set of room objects.
  const hoursMode = progressMode === "hours" && !!hoursByRoom;
  const hoursOf = (rs) => {
    let actual = 0; let budget = 0;
    for (const r of rs) for (const sc of new Set((itemsByRoom[r.key] || []).map((i) => i.scope_type || ""))) {
      const h = hoursByRoom[`${r.key}||${sc}`];
      if (h) { actual += h.actual; budget += h.budget; }
    }
    return { actual, budget };
  };
  const pctOf = (rs) => ({ ...pctFor(rs.flatMap((r) => itemsByRoom[r.key] || []), completions),
                           ...(hoursMode ? { hours: hoursOf(rs) } : {}) });

  // Building -> Floor -> Area -> [rooms]. A level RENDERS only when it still separates rows
  // somewhere in the filtered set (>= 2 distinct records inside at least one rendered parent);
  // otherwise it is dropped and its rooms move up. Identity is the full path (B1||2 is not B2||2).
  const LOCATION_LEVELS = useMemo(() => [
    { id: "bldg", field: "building", label: (v) => (v ? (/^building\s/i.test(v) ? v : `Building ${v}`) : "—"),
      bg: "var(--row-bldg-bg)", border: "border-indigo-100", text: "text-indigo-800", hover: "hover:text-indigo-900",
      sub: "text-indigo-700", strong: "text-indigo-900 dark:text-indigo-100" },
    { id: "floor", field: "floor", label: (v) => `Floor ${v || "—"}`,
      bg: "var(--row-floor-bg)", border: "border-violet-100", text: "text-violet-800", hover: "hover:text-violet-900",
      sub: "text-violet-700", strong: "text-violet-900 dark:text-violet-100" },
    { id: "area", field: "area", label: (v) => (v ? `Area ${v}` : "Unassigned"),
      bg: "var(--row-area-bg)", border: "border-sky-100", text: "text-sky-800", hover: "hover:text-sky-900",
      sub: "text-sky-700", strong: "text-sky-900 dark:text-sky-100" },
  ], []);
  // FT-3 — by room type: ONE level, every room of a type under one header. Collapsed, the header
  // reads as the type's totals with the per-room quantity when every room agrees.
  const TYPE_LEVELS = useMemo(() => [
    { id: "rtype", field: "room_type", label: (v) => (v ? String(v) : "No room type"),
      bg: "var(--row-bldg-bg)", border: "border-indigo-100", text: "text-indigo-800", hover: "hover:text-indigo-900",
      sub: "text-indigo-700", strong: "text-indigo-900 dark:text-indigo-100" },
  ], []);
  const byRoomType = groupBy === "room_type";
  // P2-3 — by scope: Scope › Building › Floor › Area › Room; every column stays (Q7).
  const byScope = groupBy === "scope";
  const SCOPE_LEVELS = useMemo(() => [
    { id: "scope", field: "_scope", label: (v) => v || "No scope",
      bg: "var(--row-bldg-bg)", border: "border-emerald-100", text: "text-emerald-800 dark:text-emerald-300", hover: "hover:text-emerald-900",
      sub: "text-emerald-700 dark:text-emerald-400", strong: "text-emerald-900 dark:text-emerald-100" },
    ...LOCATION_LEVELS,
  ], [LOCATION_LEVELS]);
  const LEVELS = byRoomType ? TYPE_LEVELS : byScope ? SCOPE_LEVELS : LOCATION_LEVELS;
  const groupDateCells = byRoomType || byScope ? null : renderGroupDateCells;
  const { shown: shownLevels, singles: singleLevels } = useMemo(() => {
    if (byRoomType) return { shown: [true], singles: [] };
    const lv = levelVisibility(rooms);
    return byScope ? { shown: [true, ...lv.shown], singles: [] } : lv;
  }, [rooms, byRoomType, byScope]);
  // Expand / collapse everything under one scope row.
  const scopeNodeKeys = (k) => {
    const prefix = k.slice(k.indexOf(":") + 1);
    return [...renderedNodeKeys].filter((x) => x === k || x.slice(x.indexOf(":") + 1).startsWith(`${prefix}||`));
  };
  const setScopeOpen = (k, open) => {
    const n = new Set(collapsed);
    const keys = scopeNodeKeys(k);
    if (open) keys.forEach((x) => n.delete(x)); else keys.forEach((x) => n.add(x));
    setCollapsed(n);
  };
  const nodeKey = (li, r) => `${LEVELS[li].id}:${LEVELS.slice(0, li + 1).map((L) => r[L.field] || "").join("||")}`;
  // Every node the CURRENT tree renders — the only keys collapse/expand ever touch.
  const renderedNodeKeys = useMemo(() => {
    const keys = new Set();
    for (const r of rooms) for (let li = 0; li < LEVELS.length; li++) if (shownLevels[li]) keys.add(nodeKey(li, r));
    return keys;
  }, [rooms, shownLevels, LEVELS]); // eslint-disable-line react-hooks/exhaustive-deps
  const groups = renderedNodeKeys; // legacy name used by the init effect below

  // Both the project tab and the installer view open fully collapsed — the field asked for the
  // headers first and the rooms on tap (2026-09-14).
  const initializedRef = useRef(false);
  // FT-3 — switching the grouping rebuilds the tree, so it opens collapsed again.
  useEffect(() => { initializedRef.current = false; }, [groupBy]);

  // Field view: the grid takes every pixel from its top edge to the bottom of the usable screen
  // (safe area respected), measured live so toolbars opening/closing never leave a blank band.
  const scrollRef = useRef(null);
  const measureRef = useRef(null);
  const [roomInfo, setRoomInfo] = useState(null);   // field view: tap a room → its plan references
  const [gridMaxH, setGridMaxH] = useState(null);
  useEffect(() => { if (installMode) measureRef.current?.(); });   // re-measure after every render (rows load, bars toggle)
  useEffect(() => {
    if (!installMode) return undefined;
    const measure = () => {
      const el = scrollRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const vh = window.visualViewport?.height || window.innerHeight;
      const sab = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--sab")) || 0;
      const shell = el.closest('[data-testid="frs-expanded-view"]');
      const reserve = shell ? (parseFloat(getComputedStyle(shell).paddingBottom) || 0) + 1 : sab;   // shell padding already holds the safe area
      setGridMaxH(Math.max(160, Math.floor(vh - top - reserve)));
    };
    measure();
    measureRef.current = measure;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(document.body);
    if (scrollRef.current?.parentElement) ro?.observe(scrollRef.current.parentElement);
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    return () => {
      measureRef.current = null;
      ro?.disconnect();
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, [installMode]);
  // Mobile reach: the frozen ROOM band must never eat the whole scrollable width. At 390px it
  // used to sit over the far-right LOCATION column at EVERY scroll offset, so the camera chip
  // could not be tapped. `tailW` is what the trailing columns (LOCATION, ON SITE, the delivery
  // numbers, % Complete) actually need; when the free width can't hold them the meta columns
  // stop being sticky so every column can be scrolled clear of them.
  const [fit, setFit] = useState({ viewW: 0, scrollW: 0, tailW: 0, frozenW: 0 });
  const fitRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const read = () => {
      const head = el.querySelector('[data-testid="grid-col-location"]')
        || el.querySelector('[data-testid="grid-col-total"]');
      let tailW = 0;
      for (let n = head; n; n = n.nextElementSibling) tailW += n.getBoundingClientRect().width;
      // The rendered frozen band is wider than the widths we ask for (table layout grows cells to
      // fit rotated headers), so it has to be measured, not computed.
      let frozenW = 0;
      const row = head?.parentElement;
      const nFrozen = (showChecks ? 2 : 0) + metaCols.length;
      for (let i = 0; row && i < nFrozen && row.children[i]; i += 1) {
        frozenW += row.children[i].getBoundingClientRect().width;
      }
      setFit((p) => (p.viewW === el.clientWidth && Math.abs(p.tailW - tailW) < 1
        && Math.abs(p.frozenW - frozenW) < 1 && p.scrollW === el.scrollWidth
        ? p : { viewW: el.clientWidth, scrollW: el.scrollWidth, tailW, frozenW }));
    };
    read();
    fitRef.current = read;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(read) : null;
    ro?.observe(el);
    window.addEventListener("resize", read);
    return () => { fitRef.current = null; ro?.disconnect(); window.removeEventListener("resize", read); };
  }, [showChecks, metaCols.length]);
  useEffect(() => { fitRef.current?.(); });
  // Nothing needs unfreezing while the grid isn't scrolling sideways at all.
  const pinned = fit.viewW === 0 || fit.scrollW <= fit.viewW + 1
    || fit.viewW - fit.frozenW >= Math.max(160, fit.tailW + 24);
  // "all" = the whole meta band is frozen (desktop / landscape, unchanged).
  // "room" = phone portrait in Material: ROOM # only.
  // "none" = on a phone the band would cover the trailing columns, so nothing is frozen.
  const pinMode = phonePortrait ? "room" : phone.narrow && !pinned ? "none" : "all";
  const sticky = (left, w, bg = "var(--sticky-cell-bg)", z = 5) => (
    pinMode === "all" ? stickyStyle(left, w, bg, z) : { minWidth: `${w}px`, maxWidth: `${w}px`, background: bg }
  );
  const metaSticky = (c, bg, z = 5) => (
    pinMode === "all" ? stickyStyle(c.left, c.w, bg, z)
      : pinMode === "room" && c.field === "room_number" ? stickyStyle(0, c.w, bg, z)
      : { minWidth: `${c.w}px`, maxWidth: `${c.w}px`, background: bg }
  );
  const stickySpan = (left, minW, bg, z, extra = {}) => ({
    background: bg,
    ...(pinMode === "all" ? { position: "sticky", left, zIndex: z, minWidth: `${minW}px` } : {}),
    ...extra,
  });
  useEffect(() => {
    if (rooms.length === 0) return;
    if (initializedRef.current) return;
    initializedRef.current = true;
    // a remembered set from another grouping holds none of this tree's keys — open collapsed then
    const fits = Array.isArray(collapsedInit) && collapsedInit.some((k) => renderedNodeKeys.has(k));
    setCollapsed(fits || (Array.isArray(collapsedInit) && !collapsedInit.length) ? new Set(collapsedInit) : new Set(renderedNodeKeys));
    // eslint-disable-next-line
  }, [renderedNodeKeys, installMode, rooms.length]);
  // A remembered set that lands after the grid has already opened (the snapshot fetch coming back
  // from a drawing link) is applied too. Value-compared, so the report-upward cannot loop.
  // A parent-applied set must not echo back up: the two effects below can fire in the same
  // commit with stale values and ping-pong forever (seen when the remembered view landed late).
  const fromParentRef = useRef(false);
  useEffect(() => {
    if (!Array.isArray(collapsedInit)) return;
    if (collapsedInit.length === collapsed.size && collapsedInit.every((k) => collapsed.has(k))) return;
    initializedRef.current = true;
    fromParentRef.current = true;
    setCollapsed(new Set(collapsedInit));
    // eslint-disable-next-line
  }, [collapsedInit]);
  // report upward so the view can be remembered (and restored after a trip to the drawings)
  useEffect(() => {
    if (!initializedRef.current) return;
    if (fromParentRef.current) { fromParentRef.current = false; return; }
    if (Array.isArray(collapsedInit) && collapsedInit.length === collapsed.size && collapsedInit.every((k) => collapsed.has(k))) return;
    onCollapsedChange?.([...collapsed]);
    // eslint-disable-next-line
  }, [collapsed]);

  const toggle = (k) => {
    const n = new Set(collapsed);
    if (n.has(k)) n.delete(k); else n.add(k);
    setCollapsed(n);
  };

  const allCollapsedSet = () => new Set(renderedNodeKeys);
  const floorsOnlySet = () => new Set([...renderedNodeKeys].filter((k) => !k.startsWith("bldg:")));
  const setsEqual = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));
  // "Collapse all" only counts nodes that are actually on screen
  const collapsedShown = new Set([...collapsed].filter((k) => renderedNodeKeys.has(k)));

  // The chevron button toggles; the level buttons always open the grid to exactly that level.
  const expandAll = () => {
    setCollapsed(collapsedShown.size === 0 ? allCollapsedSet() : new Set());
  };
  const expandToBuilding = () => setCollapsed(allCollapsedSet());
  const expandToFloor = () => setCollapsed(floorsOnlySet());
  const expandToArea = () => setCollapsed(new Set([...renderedNodeKeys].filter((k) => k.startsWith("area:"))));
  const expandToRoom = () => setCollapsed(new Set());
  const hasFloorLevel = !!shownLevels[1];
  const hasAreaLevel = !!shownLevels[2];
  // Which depth the grid is opened to — that level button stays highlighted
  const areaKeys = [...renderedNodeKeys].filter((k) => k.startsWith("area:"));
  const gridLevel = collapsedShown.size === 0 ? 3
    : areaKeys.length && setsEqual(collapsedShown, new Set(areaKeys)) ? 2.5
    : setsEqual(collapsedShown, floorsOnlySet()) ? 2 : setsEqual(collapsedShown, allCollapsedSet()) ? 1 : 0;

  const toggleRoomSelect = (r) => {
    const ids = pickable(idsByRoom[r.key] || []);
    const ns = new Set(selected);
    const allOn = ids.every((id) => ns.has(id));
    if (allOn) ids.forEach((id) => ns.delete(id));
    else ids.forEach((id) => ns.add(id));
    setSelected(ns);
  };
  const isRoomAllSelected = (r) => {
    const ids = pickable(idsByRoom[r.key] || []);
    return ids.length > 0 && ids.every((id) => selected.has(id));
  };
  const isRoomSomeSelected = (r) => {
    const ids = pickable(idsByRoom[r.key] || []);
    return ids.some((id) => selected.has(id)) && !isRoomAllSelected(r);
  };
  const toggleGroupSelect = (groupRooms) => {
    const allIds = pickable(groupRooms.flatMap((r) => idsByRoom[r.key] || []));
    const ns = new Set(selected);
    const allOn = allIds.length > 0 && allIds.every((id) => ns.has(id));
    if (allOn) allIds.forEach((id) => ns.delete(id));
    else allIds.forEach((id) => ns.add(id));
    setSelected(ns);
  };
  const groupAllSel = (groupRooms) => {
    const ids = pickable(groupRooms.flatMap((r) => idsByRoom[r.key] || []));
    return ids.length > 0 && ids.every((id) => selected.has(id));
  };

  // Readiness dot for a set of rooms (building / floor / single room).
  const ReadyDot = ({ roomsIn, testid, label }) => {
    const its = roomsIn.flatMap((r) => itemsByRoom[r.key] || []);
    const { state, ready, total } = readinessOf(its);
    if (!total) return null;
    const title = state === "partial"
      ? `${READY_LABEL.partial} — ${ready} of ${total}`
      : READY_LABEL[state];
    return (
      <button
        type="button"
        data-testid={testid}
        data-readiness={state}
        aria-label={title}
        title={`${title} — click to mark ${state === "ready" ? "Not Ready" : "Ready"}`}
        onClick={() => onSetReadiness && onSetReadiness(its.map((i) => i.id), state !== "ready", label)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200/60 transition"
      >
        <span className="block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: READY_DOT[state] }} />
      </button>
    );
  };

  const empty = items.length === 0;

  const roomTotal = (r) =>
    Object.values(QMAP[r.key] || {}).reduce((s, n) => s + n, 0);

  // What the LOCATION cell says: one place → its name, more than one → a count,
  // nothing on site → empty. Never a truncated list.
  const locationCell = (r) => {
    const names = [...(locByRoom[r.key] || new Set())];
    if (names.length === 0) return "";
    return names.length === 1 ? names[0] : `${names.length} locations`;
  };
  const locationTitle = (r) => [...(locByRoom[r.key] || new Set())].join("\n");
  const showLocation = materialMode || pickingMode;

  const selectedTotalQty = items.reduce(
    (s, it) => s + (selected.has(it.id)
      ? (DEL ? Number((deliveryMap[it.frs_item_key] || {}).released || 0) : (it.quantity || 0))
      : 0),
    0,
  );

  // FT-3 — the grid as displayed, row for row: a collapsed group is one totals row, an expanded
  // group is its header followed by whatever is under it. Same columns as the screen.
  if (exportRef) exportRef.current = onExportView && !installMode ? () => onExportView(exportView()) : null;
  const exportView = () => {
    const colLabel = (c) => headerLines(c).lines.map((l) => l.text).join(" ");
    const headers = [
      ...(byRoomType ? ["Room Type"] : []),
      ...metaCols.map((c) => c.label),
      "Rooms",
      ...columns.map((c) => `${c.scope ? `${c.scope} · ` : ""}${colLabel(c)}`),
      materialMode ? "On site" : DEL ? "Released" : "Total",
    ];
    const rows = [];
    const group_rows = [];
    const depths = [];
    const groupByLocal = (rs, li) => {
      const m = new Map();
      for (const r of rs) {
        const v = r[LEVELS[li].field] || "";
        if (!m.has(v)) m.set(v, []);
        m.get(v).push(r);
      }
      return [...m.keys()].sort(natCmp).map((v) => [v, m.get(v)]);
    };
    const walk = (rs, li, depth) => {
      if (li >= LEVELS.length) {
        for (const r of rs) {
          const q = QMAP[r.key] || {};
          rows.push([
            ...(byRoomType ? [r.room_type || ""] : []),
            ...metaCols.map((c) => r[c.field] || ""),
            "",
            ...columns.map((c) => q[c.key] || ""),
            roomTotal(r) || "",
          ]);
          depths.push(depth);
        }
        return;
      }
      if (!shownLevels[li]) return walk(rs, li + 1, depth);
      for (const [value, sub] of groupByLocal(rs, li)) {
        const k = nodeKey(li, sub[0]);
        const isCollapsed = collapsed.has(k);
        const byCol = sub.reduce((acc, r) => {
          const q = QMAP[r.key] || {};
          for (const ck of Object.keys(q)) acc[ck] = (acc[ck] || 0) + q[ck];
          return acc;
        }, {});
        group_rows.push(rows.length);
        depths.push(depth);
        rows.push([
          LEVELS[li].label(value),
          ...Array(Math.max(0, (byRoomType ? 1 : 0) + metaCols.length - 1)).fill(""),
          sub.length,
          ...columns.map((c) => (isCollapsed ? (byCol[c.key] || "") : "")),
          isCollapsed ? sub.reduce((s, r) => s + roomTotal(r), 0) : "",
        ]);
        if (!isCollapsed) walk(sub, li + 1, depth + 1);
      }
    };
    walk(rooms, 0, 0);
    const scopeName = colGroups.length === 1 ? colGroups[0].scope : "";
    const title = `${byRoomType ? "By room type" : "By location"}${scopeName ? ` ${scopeName}` : ""}`;
    return { title, headers, rows, group_rows, depths };
  };

  const lvlCls = (on) => `rounded-full px-2 py-0.5 transition-colors ${on ? "bg-[#007BFF]/10 font-semibold text-[#007BFF] dark:bg-blue-950/50 dark:text-blue-300" : "hover:text-gray-900 dark:hover:text-gray-100"}`;
  const showFilter = scopeOptions && onScopeFilter && !DEL;
  const showGroup = onGroupBy && !DEL;
  // Shared strip order (RA / Jobs / Install): expand-collapse all · Filter · | · Group · | · level jumps · Progress · summary
  const expandAllBtn = (
    <button
      onClick={expandAll}
      className="grid h-6 w-6 self-end place-items-center rounded border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
      data-testid="grid-expand-all"
      title={collapsedShown.size === 0 ? "Collapse all" : "Expand all"}
      aria-label={collapsedShown.size === 0 ? "Collapse all" : "Expand all"}
    >
      {collapsedShown.size === 0 ? <ChevronsDownUp className="h-3.5 w-3.5" /> : <ChevronsUpDown className="h-3.5 w-3.5" />}
    </button>
  );
  const strip = (
    <>
        {expandAllBtn}
        {showFilter && (
          <>
            <Cluster compact={phonePortrait} label="Filter" testId="grid-strip-filter">
              <ScopePills testId="grid-scope-pills" options={scopeOptions} value={scopeFilter || []} onChange={onScopeFilter} />
            </Cluster>
            <Divider />
          </>
        )}
        {showGroup && (
          <>
            <Cluster compact={phonePortrait} label="Group" testId="grid-strip-group">
              <button type="button" onClick={() => groupBy !== "location" && onGroupBy("location")} data-active={!byRoomType && !byScope ? "1" : "0"}
                      className={lvlCls(!byRoomType && !byScope)} data-testid="grid-group-by-location" title="Rows by Building › Floor › Area › Room">
                Location
              </button>
              <button type="button" onClick={() => !byRoomType && onGroupBy("room_type")} data-active={byRoomType ? "1" : "0"}
                      className={lvlCls(byRoomType)} data-testid="grid-group-by-room-type" title="Rows grouped by room type">
                {phonePortrait ? "Type" : "Room Type"}
              </button>
              <button type="button" onClick={() => !byScope && onGroupBy("scope")} data-active={byScope ? "1" : "0"}
                      className={lvlCls(byScope)} data-testid="grid-group-by-scope" title="Rows by Scope › Building › Floor › Area › Room">
                Scope
              </button>
            </Cluster>
            <Divider />
          </>
        )}
        {/* One expand / collapse toggle (top-left) replaces the per-level jumps and the Units / Hours switch — % complete is units only. */}
        {selectedTotalQty > 0 && (
          <span className="text-gray-700 font-medium" data-testid="grid-selected-count">
            <span className="text-gray-900 font-semibold">{selectedTotalQty}</span> selected
          </span>
        )}
        <span className={`ml-auto whitespace-normal text-right ${phonePortrait ? "hidden" : ""}`} data-testid="grid-scope-total">
          {brief
            ? `${rooms.length} room${rooms.length === 1 ? "" : "s"}`
            : DEL
            ? `${rooms.length} rooms · released ${delGrand} · placed ${delSum(rooms, "placed")} · not delivered ${delSum(rooms, "not_delivered")} · to account for ${delSum(rooms, "unaccounted")}`
            : materialMode
            ? `${rooms.length} rooms · ${siteGrand} on site`
            : `${rooms.length} rooms · ${grandTotal} units · ${grandReady} of ${grandTotal} ready`}
        </span>
    </>
  );

  if (empty) {
    return (
      <div className="space-y-0">
        {toolbarSlot ? createPortal(strip, toolbarSlot) : (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-1.5 text-xs text-gray-500 border-t border-gray-200 bg-gray-50/40 whitespace-nowrap">
            {strip}
          </div>
        )}
        {emptyState || <div className="px-6 py-12 text-center text-gray-500" data-testid="frs-grid-empty">No FRS items match the current filters.</div>}
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* TKO-2 §4 — units added on the plan with no room yet, assignable from here (same step as Finish) */}
      {projectId && !pickingMode && !hideUnassigned && <UnassignedBucket projectId={projectId} onChanged={onUnassignedChanged} />}
      {/* Field view: the strip rides in the compact bar beside the search box. */}
      {toolbarSlot ? createPortal(strip, toolbarSlot) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-1.5 text-xs text-gray-500 border-t border-gray-200 bg-gray-50/40 whitespace-nowrap">
          {strip}
        </div>
      )}
      <div ref={scrollRef}
           className={`overflow-auto border-t border-gray-200 relative ${installMode ? "" : "max-h-[72vh]"}`}
           style={installMode ? { maxHeight: gridMaxH ? `${gridMaxH}px` : "calc(100dvh - 150px)" } : undefined}
           data-testid="frs-grid-scroll">
        <table className="text-xs border-collapse w-full" style={{ borderSpacing: 0 }}>
          <thead className="sticky top-0" style={{ zIndex: 20 }}>
            {/* Scope group band */}
            <tr>
              <th
                className="bg-gray-100 border-b border-r border-gray-200 px-2 py-1"
                colSpan={metaCols.length + dateCols + (installMode ? 0 : 2)}
                style={stickySpan(0, frozenEnd, "var(--sticky-head-bg)", 25)}
              ></th>
              {colGroups.map((g, i) => (
                <th
                  key={i}
                  colSpan={g.count}
                  title={g.scope}
                  data-testid={`grid-scope-head-${g.scopeCode || g.scope}`}
                  className="bg-gray-100 border-b border-r border-gray-200 px-2 py-1 text-left text-[10px] uppercase text-blue-700 font-semibold tracking-wide"
                >
                  {g.scopeCode || g.scope || "—"}
                </th>
              ))}
              <th className="bg-gray-100 border-b border-gray-200 px-2 py-1"></th>
              {materialMode && <th className="bg-gray-100 border-b border-gray-200 px-2 py-1"></th>}
              <th className="bg-gray-100 border-b border-l border-gray-200 px-2 py-1"></th>
            </tr>
            {/* Column headers — dynamic FRS meta columns (blank ones skipped) */}
            <tr>
              {showChecks && <><th
                data-testid="grid-col-request"
                title={materialMode ? "Tick rooms to move their material" : "Request"}
                style={{ ...sticky(0, CHECK_W, "var(--sticky-head-bg)", 25), height: HEAD_H, boxSizing: "border-box" }}
                className="border-b border-r border-gray-200 align-bottom p-0 text-left"
              >
                <div
                  className="inline-flex items-start px-1 pb-2 pt-1"
                  style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                >
                  <span className="text-[11px] uppercase text-gray-600 dark:text-gray-300 font-semibold tracking-tight whitespace-nowrap">
                    {materialMode ? "Move" : "Request"}
                  </span>
                </div>
              </th></>}
              {showChecks && <th
                data-testid="grid-col-readiness"
                title="Ready"
                style={{ ...sticky(CHECK_W, READY_W, "var(--sticky-head-bg)", 25), height: HEAD_H, boxSizing: "border-box" }}
                className="border-b border-r border-gray-200 align-bottom p-0 text-left"
              >
                <div
                  className="inline-flex items-start px-1 pb-2 pt-1"
                  style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                >
                  <span className="text-[11px] uppercase text-gray-600 dark:text-gray-300 font-semibold tracking-tight whitespace-nowrap">
                    Ready
                  </span>
                </div>
              </th>}
              {metaCols.map((c) => (
                <th
                  key={c.field}
                  data-testid={`grid-col-${c.field}`}
                  title={c.label}
                  style={{ ...metaSticky(c, "var(--sticky-head-bg)", 25), height: HEAD_H, boxSizing: "border-box" }}
                  className={`border-b border-r border-gray-200 align-bottom p-0 ${c.numeric ? "text-right" : "text-left"}`}
                >
                  <div
                    className="inline-flex items-start px-1 pb-2 pt-1"
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >
                    <span
                      className="text-[11px] uppercase text-gray-600 dark:text-gray-300 font-semibold tracking-tight whitespace-nowrap"
                      style={{ maxHeight: HEAD_H - 12, overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {c.label}
                    </span>
                  </div>
                </th>
              ))}
              {!installMode && dateLabels.map((lbl, di) => (
                <th
                  key={lbl}
                  data-testid={`grid-col-${lbl.toLowerCase().replace(/\s+/g, "-")}`}
                  className="relative bg-gray-50 border-b border-r border-gray-200 align-bottom p-0 text-left"
                  style={{ height: HEAD_H, boxSizing: "border-box", minWidth: 92 }}
                >
                  <div className="inline-flex items-start px-1 pb-2 pt-1" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                    <span className="text-[11px] uppercase text-gray-600 dark:text-gray-300 font-semibold tracking-tight whitespace-nowrap">{lbl}</span>
                  </div>
                  {onToggleDates && !pickingMode && di === dateLabels.length - 1 && (
                    <button type="button" onClick={onToggleDates}
                            title={datesCollapsed ? "Show the completion columns" : "Collapse to the Start column only"}
                            className="absolute right-0.5 top-0.5 inline-flex h-4 w-4 items-center justify-center rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                            data-testid="grid-dates-toggle">
                      {datesCollapsed ? <ChevronsRight className="h-3 w-3" /> : <ChevronsLeft className="h-3 w-3" />}
                    </button>
                  )}
                </th>
              ))}

              {columns.map((c) => {
                const h = headerLines(c);
                const tint = tintByCol[c.key];
                return (
                <th
                  key={c.key}
                  data-tint={tint?.word || ""}
                  className="bg-gray-50 border-b border-r border-gray-200 align-bottom p-0 relative"
                  style={{ ...(installMode
                    ? { minWidth: 56, height: HEAD_H, boxSizing: "border-box" }
                    : { minWidth: 48, maxWidth: 72, height: HEAD_H, boxSizing: "border-box" }),
                    ...(tint ? { background: tint.head } : {}) }}
                >
                  {/* One tap / click opens the product detail — same sheet in
                      the RA view and the installer view. */}
                  <button
                    type="button"
                    onClick={() => setProductInfo(c)}
                    data-testid={`grid-product-head-${c.key}`}
                    data-header-field={h.field}
                    title={tint ? `${h.tip}\nColour from: ${tint.source} (${tint.word})` : h.tip}
                    className="w-full h-full flex items-end text-left hover:bg-blue-50/70 active:bg-blue-100/70"
                  >
                    <div
                      className="inline-flex flex-col items-start gap-1 px-1 pb-1.5 pt-1"
                      style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                    >
                      {h.lines.map((ln, i) => (
                        <span
                          key={i}
                          data-testid={i === 0
                            ? `grid-product-part-${c.key}`
                            : `grid-product-line-${i}-${c.key}`}
                          className={ln.primary
                            ? "text-[11px] text-gray-900 dark:text-gray-100 font-semibold tracking-tight"
                            : "text-[10px] text-gray-500 dark:text-gray-400 font-normal"}
                          style={{ maxHeight: HEAD_H - 14, whiteSpace: "nowrap",
                                   overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {ln.text}
                        </span>
                      ))}
                    </div>
                  </button>
                  {pickingMode && (
                    <div className="absolute top-0 left-0 right-0 flex flex-col items-center gap-0.5 pt-0.5">
                      {onPickProduct && (
                        <input
                          type="checkbox"
                          className="w-3 h-3"
                          title="Select this product in every ticked room"
                          data-testid={`pull-product-check-${c.key}`}
                          checked={rooms.some((r) => (itemsByRC[`${r.key}|${c.key}`] || [])
                            .some((it) => pickSel[it.frs_item_key]))}
                          onChange={() => onPickProduct(c, rooms.flatMap((r) => itemsByRC[`${r.key}|${c.key}`] || []))}
                        />
                      )}
                      {(() => {
                        const oh = onHand[c.uid] || {};
                        const avail = Number(oh.available || 0);
                        const demand = Number(oh.demand || 0);
                        const tone = demand === 0 ? "bg-gray-300"
                          : avail >= demand ? "bg-emerald-500"
                          : avail > 0 ? "bg-amber-500" : "bg-red-500";
                        return (
                          <span
                            data-testid={`pull-on-hand-${c.key}`}
                            data-on-hand={avail}
                            title={`On hand ${avail} · demand ${demand}`
                                   + (oh.inbound ? ` · inbound ${oh.inbound}` : "")}
                            className={`w-2 h-2 rounded-full ${tone}`}
                          />
                        );
                      })()}
                    </div>
                  )}
                </th>
                );
              })}
              {showLocation && (
                <th
                  data-testid="grid-col-location"
                  className="bg-gray-50 border-b border-gray-200 px-2 py-1 text-left text-[10px] uppercase text-gray-500 font-medium"
                  style={{ minWidth: 130 }}
                >
                  {pickingMode ? "Where it is" : "Location"}
                </th>
              )}
              <th data-testid="grid-col-total" className="bg-gray-50 border-b border-gray-200 align-bottom p-0 text-right" style={{ minWidth: 36, height: HEAD_H, boxSizing: "border-box" }}>
                <div className="inline-flex items-start px-1 pb-2 pt-1" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                  <span className="text-[10px] uppercase text-gray-500 font-medium whitespace-nowrap">{qtyLabel || (materialMode ? "On site" : "Total")}</span>
                </div>
              </th>
              {DEL && DEL_FIELDS.slice(1).map((f) => (
                <th key={f} data-testid={`grid-col-delivery-${f}`}
                    className="bg-gray-50 border-b border-l border-gray-200 align-bottom p-0 text-right"
                    style={{ minWidth: 36, height: HEAD_H, boxSizing: "border-box" }}>
                  <div className="inline-flex items-start px-1 pb-2 pt-1" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                    <span className="text-[10px] uppercase text-gray-500 font-medium whitespace-nowrap">{DEL_LABELS[f]}</span>
                  </div>
                </th>
              ))}
              <th
                data-testid="grid-col-pct-complete"
                className="bg-gray-50 border-b border-l border-gray-200 align-bottom p-0 text-right"
                style={{ minWidth: 36, height: HEAD_H, boxSizing: "border-box" }}
              >
                <div className="inline-flex items-start px-1 pb-2 pt-1" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                  <span className="text-[10px] uppercase text-gray-500 font-medium whitespace-nowrap">{hoursMode ? "% Hours" : "% Complete"}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* SINGLE-VALUE HIERARCHY CONTEXT — the levels that resolve to one value, consolidated
                into ONE row directly above the rooms, in the same format as a normal level header
                (label · rooms · units · ready). It is not a tree node: no expand control, no
                checkbox, no readiness dot, no product totals. The scope rides on it when the whole
                filtered set is one scope. */}
            {singleLevels.length > 0 && (() => {
              const L = LEVELS.find((x) => x.id === singleLevels[singleLevels.length - 1].id) || LEVELS[0];
              const crumbs = [scopeCrumb(items), ...singleLevels.map((s) => s.label(s.value))].filter(Boolean);
              const total = rooms.reduce((s, r) => s + roomTotal(r), 0);
              const ready = rooms.reduce((s, r) => s + (readyQtyByRoom[r.key] || 0), 0);
              // Hierarchy rows carry the room count and the unit count only — the
              // delivery / readiness breakdown lives in the columns, not here.
              const summary = `${rooms.length} room${rooms.length === 1 ? "" : "s"} · ${total} unit${total === 1 ? "" : "s"}`;
              return (
                <tr className={`border-b ${L.border}`} data-testid="grid-context-row"
                    data-levels={singleLevels.map((s) => `${s.id}:${s.value}`).join("|")}>
                  {showChecks && <td className={`px-0.5 py-1 border-r text-center ${L.border}`} style={sticky(0, CHECK_W, L.bg, 5)}>
                    {/* same as a level header: tick everything below it */}
                    <input type="checkbox" data-testid="grid-check-context"
                           title="Select every room below"
                           checked={groupAllSel(rooms)} onChange={() => toggleGroupSelect(rooms)} />
                  </td>}
                  {showChecks && <td className={`px-0.5 py-1 border-r ${L.border}`} style={sticky(CHECK_W, READY_W, L.bg, 5)}></td>}
                  {/* Phone portrait: the frozen ROOM # column keeps its own narrow cell so the
                      label below can never widen it. */}
                  {phonePortrait && metaCols[0] && (
                    <td className={`border-r ${L.border}`} style={metaSticky(metaCols[0], L.bg, 5)}></td>
                  )}
                  <td colSpan={metaCols.length + dateCols - (phonePortrait && metaCols[0] ? 1 : 0)}
                      className={`px-2 py-1 font-semibold ${L.text}`}
                      style={stickySpan(CHECK_W + READY_W, frozenEnd - CHECK_W - READY_W, L.bg, 5)}>
                    <span className="inline-flex items-center gap-1 max-w-full truncate">
                      <span data-testid="grid-context-label">{crumbs.join(" · ")}</span>
                      <span className={`text-[10px] font-normal ${L.sub} ml-1`} data-testid="grid-context-summary">{summary}</span>
                    </span>
                  </td>
                  <td colSpan={columns.length + QTY_COLS + LOC_COLS + (pickingMode ? 1 : 0)} style={{ background: L.bg }}></td>
                  <PctCell {...pctOf(rooms)} testid="grid-pct-context" bg={L.bg} />
                </tr>
              );
            })()}
            {countFor && <GroupCountDialog group={countFor} columns={columns} qmap={QMAP} onClose={() => setCountFor(null)} />}
            {(() => {
              // Rooms grouped level by level; a hidden level passes its rooms straight through.
              const sortVals = (li, vals) => vals.sort((a, b) =>
                (li === 2 && (a === "") !== (b === "")) ? (a === "" ? 1 : -1) : natCmp(a, b));
              const groupBy = (rs, li) => {
                const m = new Map();
                for (const r of rs) {
                  const v = r[LEVELS[li].field] || "";
                  if (!m.has(v)) m.set(v, []);
                  m.get(v).push(r);
                }
                return sortVals(li, [...m.keys()]).map((v) => [v, m.get(v)]);
              };
              const renderRoom = (r) => {
                const sel = isRoomAllSelected(r);
                const partSel = isRoomSomeSelected(r);
                const conflict = (itemsByRoom[r.key] || []).some((i) => i.date_conflict);
                const rowBg = sel ? "var(--row-sel-bg)"
                  : conflict ? "var(--row-conflict-bg)"
                  : "var(--sticky-cell-bg)"; // blue when selected, amber when ABS finishes after GC
                return (
                  <tr key={r.key} className="border-b border-gray-100 hover:bg-blue-50/40">
                    {showChecks && <td className="px-0.5 py-1 border-r border-gray-200 text-center" style={sticky(0, CHECK_W, rowBg)}>
                      <input
                        type="checkbox"
                        data-testid={`grid-check-room-${r.key}`}
                        ref={(el) => { if (el) el.indeterminate = partSel; }}
                        checked={sel}
                        onChange={() => toggleRoomSelect(r)}
                      />
                    </td>}
                    {showChecks && <td
                      className="px-0.5 py-1 border-r border-gray-200 text-center"
                      style={sticky(CHECK_W, READY_W, rowBg)}
                    >
                      <ReadyDot roomsIn={[r]} testid={`grid-readiness-room-${r.key}`} label={r.room_name || r.room_number || "room"} />
                    </td>}
                    {metaCols.map((c) => (
                      <td
                        key={c.field}
                        className={`relative px-2 py-1 border-r border-gray-200 dark:border-gray-700 truncate ${c.field === "room_number" ? "font-medium text-gray-900 dark:text-gray-100" : c.field === "room_type" ? "text-gray-600 dark:text-gray-300" : "text-gray-900 dark:text-gray-100"} ${c.numeric ? "text-right tabular-nums" : ""}`}
                        style={{ ...metaSticky(c, rowBg),
                                 maxWidth: installMode && c.field === chipField ? undefined : "11ch" }}
                        title={r[c.field] || ""}
                      >
                        {editMetaCell && editMetaCell(r, c.field) ? editMetaCell(r, c.field)
                         : (c.field === "room_name" || c.field === "room_number") ? (
                          <button type="button" onClick={(e) => { e.stopPropagation(); setRoomInfo(r); }}
                                  className="max-w-full truncate text-left underline decoration-dotted decoration-gray-400 underline-offset-2 active:text-blue-700"
                                  title="Tap for this room's plan references"
                                  data-testid={`install-room-open-${c.field}-${r.key}`}>{r[c.field]}</button>
                        ) : PLAN_FIELDS.has(c.field) && r[c.field] && onOpenPlan ? (
                          planLinks?.sheets?.has(String(r[c.field]).replace(/\s+/g, "").toUpperCase()) ? (
                            <button type="button" className="text-blue-700 underline decoration-dotted hover:text-blue-900"
                                    onClick={(e) => { e.stopPropagation(); onOpenPlan({ sheet: r[c.field], room: r.room_number }); }}
                                    data-testid={`plan-link-${c.field}-${r.key}`}>{r[c.field]}</button>
                          ) : (
                            <span title="no drawing for this sheet reference">{r[c.field]}<span className="ml-1 text-[9px] text-amber-700 bg-amber-100 border border-amber-200 rounded px-0.5" data-testid={`plan-missing-${c.field}-${r.key}`}>no drawing</span></span>
                          )
                        ) : r[c.field]}
                        {c.field === "room_number" && editRowAction && editRowAction(r)}
                        {c.field === "room_number" && onOpenPlan && planLinks?.rooms?.has(String(r.room_number || "")) && (
                          <button type="button" className="ml-1 text-[9px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 hover:bg-blue-100"
                                  title="Open this room on the plan" data-testid={`plan-link-room-${r.key}`}
                                  onClick={(e) => { e.stopPropagation(); onOpenPlan({ room: r.room_number }); }}>plan</button>
                        )}
                        {c.field === "room_number" && shopLinks && <FieldShopLinks links={shopLinks} roomNumber={r.room_number} projectId={projectId} from={linkFrom} className="ml-1 align-middle" />}
                        {c.field === "room_number" && punchCounts[r.key] > 0 && (
                          <button type="button"
                            onClick={(e) => { e.stopPropagation(); onPunchRoom?.(r); }}
                            className="absolute right-0.5 top-1/2 z-[6] -translate-y-1/2 text-[9px] font-bold text-white bg-red-600 border border-red-700 rounded-full px-1 leading-4 hover:bg-red-700 shadow-sm"
                            title={`${punchCounts[r.key]} open punch item${punchCounts[r.key] === 1 ? "" : "s"} — click for this room's punch list`}
                            data-testid={`grid-punch-badge-${r.key}`}
                          >
                            PL {punchCounts[r.key]}
                          </button>
                        )}
                        {c.field === "room_number" && (itemsByRoom[r.key] || []).some((i) => i.material_short) && (
                          <span
                            className="ml-1 text-[9px] font-bold text-red-900 bg-red-200 border border-red-300 rounded-full px-1"
                            data-testid={`grid-short-${r.key}`}
                            title={(itemsByRoom[r.key] || []).find((i) => i.material_short)?.material_short_note
                              || "The FRS asks for more than has been requested on a pull ticket"}
                          >
                            SHORT
                          </span>
                        )}
                      </td>
                    ))}
                    {(installMode || dateLabels.length === 0 ? null : renderDateCells
                      ? renderDateCells(itemsByRoom[r.key] || [], { editable: !installMode })
                      : dateLabels.map((_, di) => <td key={di} />))}
                    {columns.map((c) => {
                      const q = QMAP[r.key]?.[c.key] || 0;
                      const demand = qtyMap[r.key]?.[c.key] || 0;
                      const it = itemByRC[`${r.key}|${c.key}`];
                      const bg = it ? readinessBg(it.readiness_status) : "";
                      const comp = completions[`${r.key}||${c.scope}`];
                      // Cluster 12 — one X per COMPLETED item. A partial
                      // completion lists what is still remaining, so any
                      // item not in that list is installed.
                      // Installed units for this cell = every row of this
                      // product in this room, minus what is still remaining.
                      // A cell is only crossed out when ALL its units are in.
                      const cellRows = itemsByRC[`${r.key}|${c.key}`] || [];
                      const installed = cellRows.reduce(
                        (sum, row) => sum + rowInstalled(row, comp), 0);
                      // PLAN-COMPLETE — a pending change from the plan: the cell shows where the
                      // tapped count lands, highlighted, until it is saved or cancelled.
                      const pendInstalled = pendingItems
                        ? cellRows.reduce((sum, row) => sum + (pendingItems[row.id] !== undefined
                            ? Number(pendingItems[row.id]) : rowInstalled(row, comp)), 0)
                        : installed;
                      const pending = !materialMode && q > 0 && pendInstalled !== installed;
                      const shownInstalled = pending ? pendInstalled : installed;
                      const done = !materialMode && q > 0 && shownInstalled >= q;
                      const partly = !materialMode && q > 0 && shownInstalled > 0 && shownInstalled < q;
                      const tappable = (materialMode ? demand : q) > 0 && onCellAction;
                      // FLD-4 — on site and uninstalled. Green once something is
                      // there, amber when more arrived than the FRS now asks for.
                      const surplus = materialMode
                        ? cellRows.reduce((s, row) =>
                            s + Number((onSite[row.frs_item_key] || {}).surplus || 0), 0)
                        : 0;
                      const matTone = materialMode && !DEL && q > 0
                        ? (surplus > 0 ? "var(--cell-warn-bg)" : "var(--cell-ok-bg)") : "";
                      // FLD-3 Delivery — the cell carries the ticket's numbers whether or not the
                      // material has landed: green once it is all placed, amber while some is
                      // still to account for, red when something never arrived.
                      const del = DEL ? (delCellByRC[`${r.key}|${c.key}`] || null) : null;
                      const delTone = del && q > 0
                        ? (del.not_delivered > 0 ? "var(--cell-bad-bg)"
                          : del.unaccounted <= 0 ? "var(--cell-ok-bg)"
                          : del.placed > 0 ? "var(--cell-warn-bg)" : "")
                        : "";
                      // FLD-2 pulling sheet: yellow = already requested and not
                      // yet on site, green = on site, red = nothing on hand to
                      // cover it. Anything untouched stays plain.
                      const mat = pickingMode && q > 0 ? (() => {
                        const state = cellRows.reduce((acc, row) => {
                          const l = (cellState.lines || {})[row.frs_item_key] || {};
                          acc.requested += Number(l.requested || 0);
                          acc.delivered += Number(l.delivered || 0);
                          acc.short += Number(l.short || 0);
                          (l.reasons || []).forEach((x) => acc.reasons.add(x));
                          (l.tickets || []).forEach((x) => acc.tickets.add(x));
                          (l.locations || []).forEach((x) => acc.locations.add(x));
                          return acc;
                        }, { requested: 0, delivered: 0, short: 0,
                             reasons: new Set(), tickets: new Set(), locations: new Set() });
                        const avail = Number((cellState.on_hand || {})[c.uid] || 0);
                        if (state.delivered >= q && q > 0) {
                          return { tone: "var(--cell-ok-bg)", state: "on-site",
                                   note: `${state.delivered} on site`
                                         + (state.locations.size ? ` · at ${[...state.locations].join("; ")}` : "") };
                        }
                        // the warehouse looked and could not provide it:
                        // red, but flagged as already requested
                        if (state.short > 0) {
                          return {
                            tone: "var(--cell-bad-bg)", state: "short", flag: true,
                            ticket: [...state.tickets][0] || "",
                            note: `${state.short} short (${[...state.reasons].join(", ") || "shortfall"})`
                                  + ` · requested on ${[...state.tickets].join(", ")}`
                                  + " — click the flag to open the ticket",
                          };
                        }
                        if (state.requested > 0) {
                          return { tone: "var(--cell-warn-bg)", state: "requested",
                                   note: `${state.requested} requested`
                                         + (state.delivered ? `, ${state.delivered} on site` : "")
                                         + ([...state.tickets].length ? ` · ${[...state.tickets].join(", ")}` : "") };
                        }
                        if (avail <= 0) {
                          return { tone: "var(--cell-bad-bg)", state: "not-on-hand",
                                   note: "nothing on hand" };
                        }
                        return { tone: "", state: "available",
                                 note: `${avail} on hand` };
                      })() : null;
                      return (
                        <td
                          key={c.key}
                          data-testid={`grid-cell-${r.key}-${c.key}`}
                          data-col={c.key}
                          onClick={pickingMode
                            ? (q > 0 && onPickCells
                              ? () => onPickCells(itemsByRC[`${r.key}|${c.key}`] || [])
                              : undefined)
                            : (tappable && !editCell ? () => onCellAction(r, c.scope, itemsByRoom[r.key] || []) : undefined)}
                          title={mat ? mat.note : del
                            ? `released ${del.released} · placed ${del.placed} · not delivered ${del.not_delivered} · to account for ${del.unaccounted}`
                            : materialMode
                            ? (q > 0
                              ? `${q} on site and uninstalled of ${demand} required`
                                + (surplus > 0 ? ` · ${surplus} surplus` : "")
                              : demand > 0 ? "nothing on site yet" : "")
                            : pending ? `${shownInstalled} of ${q} installed once this is saved (${installed} of ${q} saved now) — tap to adjust or add photos`
                            : (tappable ? (installMode ? "Tap to complete this room / scope" : "Click to view completion, photos and notes") : "")}
                          data-changed={changedKeys && (itemsByRC[`${r.key}|${c.key}`] || [])
                            .some((it) => changedKeys.has(it.frs_item_key)) ? "1" : undefined}
                          data-pending={pending ? `${shownInstalled}/${q}` : undefined}
                          data-material-state={mat ? mat.state : undefined}
                          data-on-site={materialMode ? q : undefined}
                          data-delivery={del ? `${del.released}/${del.placed}/${del.not_delivered}/${del.unaccounted}` : undefined}
                          className={`relative border-r border-gray-100 dark:border-gray-700 px-1 text-center tabular-nums whitespace-nowrap ${installMode ? "py-2" : "py-1"} ${q === 0 ? "text-gray-500 dark:text-gray-400" : "text-gray-900 dark:text-gray-100 font-medium"} ${tappable ? "cursor-pointer hover:bg-blue-100/60" : ""}`}
                          style={{ minWidth: installMode ? 44 : 48,
                            ...(mat?.tone ? { background: mat.tone }
                                : delTone ? { background: delTone }
                                : matTone ? { background: matTone }
                                : bg ? { background: bg }
                                : tintByCol[c.key] ? { background: tintByCol[c.key].cell } : {}) }}
                        >
                          {/* FLD-PHOTO Part 2 — the installer's photos, read straight off the same
                              completion record. The badge only says they exist; opening it changes
                              nothing about the completion. */}
                          {!materialMode && !pickingMode && onInstallPhotos && (() => {
                            const shots = (comp?.photos || []).filter((p) => !p.is_deleted);
                            if (!shots.length || q <= 0) return null;
                            return (
                              <button type="button" data-testid={`grid-install-photos-${r.key}-${c.key}`}
                                      title={`${shots.length} installation photo(s) — ${comp.mode === "full" ? "100%" : "partial"}`}
                                      onClick={(ev) => { ev.stopPropagation(); onInstallPhotos({ room: r, scope: c.scope, completion: comp }); }}
                                      className="absolute bottom-0 right-0 inline-flex items-center rounded-tl border border-blue-300 bg-white/90 px-[1px] text-blue-800">
                                <Camera className="w-2.5 h-2.5" />
                                <span className="text-[8px] font-semibold tabular-nums">{shots.length}</span>
                              </button>
                            );
                          })()}
                          {pickingMode && q > 0 ? (() => {
                            const rowsHere = itemsByRC[`${r.key}|${c.key}`] || [];
                            const pulled = rowsHere.reduce(
                              (a, it) => a + (pickSel[it.frs_item_key] || 0), 0);
                            return (
                              <>
                              <span
                                data-testid={`pull-cell-${r.key}-${c.key}`}
                                data-picked={pulled}
                                className={pulled > 0
                                  ? "inline-block min-w-[18px] rounded bg-blue-600 text-white font-semibold px-1"
                                  : ""}
                              >
                                {pulled > 0 ? pulled : q}
                              </span>
                                {mat?.flag && (
                                  /* red because the warehouse could not
                                     provide it, flagged because it HAS been
                                     requested — the foreman must chase it,
                                     not re-request it. */
                                  <span
                                    data-testid={`pull-flag-${r.key}-${c.key}`}
                                    title={mat.note}
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      if (onChaseTicket && mat.ticket) {
                                        onChaseTicket(mat.ticket.split(" ")[0]);
                                      }
                                    }}
                                    className="absolute top-0 right-0 w-0 h-0 border-t-[9px] border-l-[9px] border-t-amber-500 border-l-transparent cursor-pointer"
                                  />
                                )}
                              </>
                            );
                          })() : pending ? (
                            <span className="inline-block min-w-[26px] rounded bg-emerald-600 text-white font-bold px-1 ring-2 ring-emerald-300"
                                  data-testid={`grid-cell-pending-${r.key}-${c.key}`}>
                              {shownInstalled} of {q}
                            </span>
                          ) : done && q > 0 ? (
                            <>
                              <span className="text-gray-400">{q}</span>
                              {/* The ✕ is drawn corner to corner so a completed
                                  cell reads at a glance on a phone. */}
                              <svg
                                viewBox="0 0 100 100"
                                preserveAspectRatio="none"
                                aria-hidden="true"
                                data-testid={`grid-cell-x-${r.key}-${c.key}`}
                                className="absolute inset-0 w-full h-full text-emerald-600 pointer-events-none"
                              >
                                <line x1="2" y1="2" x2="98" y2="98" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                                <line x1="98" y1="2" x2="2" y2="98" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                              </svg>
                            </>
                          ) : partly ? (
                            <span className="text-amber-700 font-semibold" data-testid={`grid-cell-partial-${r.key}-${c.key}`}>
                              {installed}/{q}
                            </span>
                          ) : cellRows.some((row) => Number(((cellState.lines || {})[row.frs_item_key] || {}).short || 0) > 0)
                            ? <span className="text-red-600 font-semibold" title="Material not here — the warehouse came up short" data-testid={`grid-cell-short-${r.key}-${c.key}`}>{q || ""}</span>
                            : (q || "")}
                          {editCell ? editCell(r, c, itemsByRC[`${r.key}|${c.key}`] || [], q) : null}
                        </td>
                      );
                    })}
                    {showLocation && (() => {
                      const names = [...(locByRoom[r.key] || new Set())];
                      const shorts = notOnHandByRoom[r.key] || [];
                      const surplusHere = siteSurplusByRoom[r.key] || 0;
                      const clickable = names.length > 0 && !!onLocationCell;
                      const lineKeys = (itemsByRoom[r.key] || []).map((it) => it.frs_item_key).filter(Boolean);
                      return (
                        <td
                          data-testid={`grid-location-${r.key}`}
                          data-locations={locationCell(r)}
                          title={locationTitle(r)}
                          className={`px-2 py-1 border-l border-gray-200 align-top text-[11px] ${
                            pickingMode && names.length ? "text-emerald-800" : "text-gray-400"}`}
                          style={{ minWidth: 130, maxWidth: pickingMode ? 260 : 220 }}
                        >
                          <span className="inline-flex items-start gap-1 max-w-full">
                            <LocationCell
                              locations={names}
                              countOf={(label) => photoCounts?.by_room_location?.[`${locKey(label)}|${roomKey3(r)}`] || 0}
                              testid={`grid-where-${r.key}`}
                              onOpenDetail={clickable ? () => onLocationCell(r, itemsByRoom[r.key] || []) : undefined}
                              onOpenPhotos={onLocationPhotos
                                ? (label) => onLocationPhotos({ label, room: r, frs_item_keys: lineKeys })
                                : undefined}
                            />
                            {surplusHere > 0 && (
                              <span data-testid={`grid-surplus-${r.key}`}
                                    title={`${surplusHere} unit(s) more on site than the FRS now asks for`}
                                    className="px-1 rounded text-[9px] font-semibold bg-amber-100 text-amber-900 border border-amber-300 shrink-0">
                                surplus
                              </span>
                            )}
                          </span>
                          {shorts.map((n, i) => (
                            <div key={i} data-testid={`grid-not-on-hand-${r.key}-${i}`}
                                 className={`text-[10px] leading-tight mt-0.5 ${n.followup ? "text-blue-700" : "text-red-700"}`}>
                              {n.followup ? (
                                <>received, follow-up {n.followup} pending pull</>
                              ) : (
                                <>
                                  {n.qty} {n.product_short} not pulled — Not On Hand
                                  {n.po_ref ? `, ${n.po_ref}` : ""}
                                  {n.eta ? ` ETA ${n.eta}` : ""}
                                </>
                              )}
                            </div>
                          ))}
                          {(returnedByRoom[r.key] || []).map((n, i) => (
                            <div key={`ret-${i}`} data-testid={`grid-returned-${r.key}-${i}`}
                                 title={[n.tickets.length ? `Ticket(s): ${n.tickets.join(", ")}` : "",
                                         n.reasons.length ? `Reason(s): ${n.reasons.join(", ")}` : "",
                                         "The room still needs it — it is open and can be requested again."]
                                        .filter(Boolean).join("\n")}
                                 className="text-[10px] leading-tight mt-0.5 text-amber-800">
                              {n.returning > 0 && <>{n.returning} {n.product_short} returning to warehouse · </>}
                              {n.reopened > 0 && <>{n.reopened} open again</>}
                              {n.not_delivered > 0 && <> · {n.not_delivered} not delivered</>}
                            </div>
                          ))}
                        </td>
                      );
                    })()}
                    <td className="px-2 py-1 text-right tabular-nums font-semibold text-gray-700 bg-gray-50/70"
                        data-testid={DEL ? `grid-delivery-released-${r.key}` : undefined}>
                      {roomTotal(r)}
                    </td>
                    {DEL && DEL_FIELDS.slice(1).map((f) => {
                      const v = Number((delByRoom[r.key] || {})[f] || 0);
                      return (
                        <td key={f} data-testid={`grid-delivery-${f}-${r.key}`}
                            className={`px-2 py-1 border-l border-gray-200 text-right tabular-nums font-semibold bg-gray-50/70 ${
                              f === "placed" ? "text-emerald-700" : f === "not_delivered" ? "text-red-700"
                              : v > 0 ? "text-amber-700" : "text-gray-400"}`}>
                          {v}
                        </td>
                      );
                    })}
                    <PctCell {...pctOf([r])} testid={`grid-pct-room-${r.key}`} />
                  </tr>
                );
              };
              const header = (li, value, rs, k, depth, isCollapsed) => {
                const L = LEVELS[li];
                const total = rs.reduce((s, r) => s + roomTotal(r), 0);
                const ready = rs.reduce((s, r) => s + (readyQtyByRoom[r.key] || 0), 0);
                const idSuffix = k.slice(k.indexOf(":") + 1).replace(/\|\|/g, "-");
                const byCol = isCollapsed
                  ? rs.reduce((acc, r) => {
                      const q = QMAP[r.key] || {};
                      for (const ck of Object.keys(q)) acc[ck] = (acc[ck] || 0) + q[ck];
                      return acc;
                    }, {})
                  : null;
                const crumbs = LEVELS.slice(0, li + 1).filter((X, i) => shownLevels[i])
                  .map((X) => X.label(rs[0][X.field] || "")).join(" › ");
                return (
                  <tr key={k} className={`border-b ${L.border}`} data-testid={`grid-group-${k}`} data-level={L.id} data-depth={depth} data-collapsed={isCollapsed ? "1" : "0"}>
                    {showChecks && <td className={`px-0.5 py-1 border-r text-center ${L.border}`} style={sticky(0, CHECK_W, L.bg, 5)}>
                      <input type="checkbox" data-testid={`grid-check-${L.id}-${idSuffix}`}
                             checked={groupAllSel(rs)} onChange={() => toggleGroupSelect(rs)} />
                    </td>}
                    {showChecks && <td className={`px-0.5 py-1 border-r ${L.border} text-center`} style={sticky(CHECK_W, READY_W, L.bg, 5)}>
                      <ReadyDot roomsIn={rs} testid={`grid-readiness-${L.id}-${idSuffix}`} label={crumbs} />
                    </td>}
                    {phonePortrait && metaCols[0] && (
                      <td className={`border-r ${L.border}`} style={metaSticky(metaCols[0], L.bg, 5)}></td>
                    )}
                    <td colSpan={metaCols.length + (groupDateCells ? 0 : dateCols) - (phonePortrait && metaCols[0] ? 1 : 0)}
                        className={`px-2 py-1 ${li === 0 ? "font-semibold" : "font-medium"} ${L.text}`}
                        style={stickySpan(CHECK_W + READY_W, frozenEnd - CHECK_W - READY_W, L.bg, 5,
                                          { paddingLeft: `${8 + depth * 16}px` })}>
                      {L.id === "scope" && (() => {
                        const fullyOpen = scopeNodeKeys(k).every((x) => !collapsed.has(x));      // one toggle: everything in the scope open ↔ shut
                        return (
                          <button type="button" onClick={() => setScopeOpen(k, !fullyOpen)} title={fullyOpen ? `Collapse everything in ${value}` : `Expand everything in ${value}`}
                                  data-testid={`grid-scope-toggle-${idSuffix}`} data-open={fullyOpen ? "1" : "0"}
                                  className="mr-1 inline-grid h-5 w-5 place-items-center align-middle rounded border border-emerald-300 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-900/40">
                            {fullyOpen ? <ChevronsDownUp className="h-3 w-3" /> : <ChevronsUpDown className="h-3 w-3" />}
                          </button>
                        );
                      })()}
                      <button onClick={() => toggle(k)} className={`inline-flex items-center gap-1 ${L.hover}`}
                              data-testid={`grid-toggle-${L.id}-${idSuffix}`}>
                        {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        <span className="whitespace-nowrap">{L.label(value)}</span>
                        <span className={`text-[10px] font-normal whitespace-nowrap ${L.sub} ml-1`} data-testid={`grid-group-summary-${idSuffix}`}>
                          {`${rs.length} room${rs.length === 1 ? "" : "s"} · ${total} unit${total === 1 ? "" : "s"}`}
                        </span>
                      </button>
                    </td>
                    {groupDateCells ? groupDateCells({ level: L.id === "bldg" ? "building" : L.id, rooms: rs }) : null}
                    {isCollapsed ? (
                      <>
                        {columns.map((c) => {
                          const q = byCol[c.key] || 0;
                          // Every room of the group carrying the same quantity → say so ("2 each").
                          const per = rs.map((r) => (QMAP[r.key] || {})[c.key] || 0);
                          const each = rs.length > 1 && q > 0 && per.every((v) => v === per[0]) ? per[0] : null;
                          const bulk = onBulkCell && !DEL && !materialMode && !pickingMode;
                          return (
                            <td key={c.key} data-testid={`grid-${L.id}-total-${idSuffix}-${c.key}`}
                                className={`border-r ${L.border} dark:border-gray-700 px-1 py-1 text-center tabular-nums font-semibold whitespace-nowrap leading-tight ${
                                  bulk ? "cursor-pointer ring-1 ring-inset ring-amber-300/70 hover:bg-amber-100 dark:hover:bg-amber-950"
                                  : q === 0 ? "text-gray-300 dark:text-gray-500" : `${L.strong} cursor-pointer hover:underline`}`}
                                style={{ minWidth: 48, background: L.bg }}
                                title={bulk ? `Set this product in every room of ${L.label(value)}`
                                  : q ? `Quick count of everything in ${L.label(value)}` : ""}
                                onClick={bulk ? () => onBulkCell({ label: crumbs, rooms: rs, col: c })
                                  : q ? () => setCountFor({ label: crumbs, rooms: rs }) : undefined}>
                              {q || (bulk ? <span className="text-gray-300">+</span> : "")}
                              {each !== null && (
                                <div className={`text-[9px] font-normal ${L.sub}`} data-testid={`grid-${L.id}-each-${idSuffix}-${c.key}`}>
                                  {each} each
                                </div>
                              )}
                            </td>
                          );
                        })}
                        {(materialMode || pickingMode) && <td style={{ background: L.bg }}></td>}
                        <td className={`px-2 py-1 text-right tabular-nums font-semibold whitespace-nowrap ${L.strong} ${total ? "cursor-pointer hover:underline" : ""}`}
                            style={{ background: L.bg }} data-testid={`grid-${L.id}-grand-${idSuffix}`}
                            onClick={total ? () => setCountFor({ label: crumbs, rooms: rs }) : undefined}>
                          {total || ""}
                        </td>
                        {DEL && DEL_FIELDS.slice(1).map((f) => (
                          <td key={f} data-testid={`grid-${L.id}-delivery-${f}-${idSuffix}`}
                              className={`px-2 py-1 border-l text-right tabular-nums font-semibold whitespace-nowrap ${L.border} ${
                                f === "placed" ? "text-emerald-800" : f === "not_delivered" ? "text-red-800" : L.strong}`}
                              style={{ background: L.bg }}>
                            {delSum(rs, f) || ""}
                          </td>
                        ))}
                        <PctCell {...pctOf(rs)} testid={`grid-pct-${L.id}-${idSuffix}`} bg={L.bg} />
                      </>
                    ) : (
                      <>
                        <td colSpan={columns.length + QTY_COLS + LOC_COLS + (pickingMode ? 1 : 0)} style={{ background: L.bg }}></td>
                        <PctCell {...pctOf(rs)} testid={`grid-pct-${L.id}-${idSuffix}`} bg={L.bg} />
                      </>
                    )}
                  </tr>
                );
              };
              const renderLevel = (rs, li, depth) => {
                if (li >= LEVELS.length) return rs.map(renderRoom);
                if (!shownLevels[li]) return renderLevel(rs, li + 1, depth);
                return groupBy(rs, li).map(([value, sub]) => {
                  const k = nodeKey(li, sub[0]);
                  const isCollapsed = collapsed.has(k);
                  return (
                    <React.Fragment key={k}>
                      {header(li, value, sub, k, depth, isCollapsed)}
                      {!isCollapsed && renderLevel(sub, li + 1, depth + 1)}
                    </React.Fragment>
                  );
                });
              };
              return renderLevel(rooms, 0, 0);
            })()}
            {/* Grand totals row */}
            <tr className="font-semibold text-gray-700 sm:sticky sm:bottom-0" style={{ zIndex: 15 }}>
              {showChecks &&
              <td className="border-t border-gray-300 px-0.5 py-1.5" style={sticky(0, CHECK_W, "var(--sticky-head-bg)", 15)}></td>}
              {showChecks &&
              <td className="border-t border-gray-300 px-0.5 py-1.5" style={sticky(CHECK_W, READY_W, "var(--sticky-head-bg)", 15)}></td>}
              {phonePortrait && metaCols[0] && (
                <td className="border-t border-gray-300" style={metaSticky(metaCols[0], "var(--sticky-head-bg)", 15)}></td>
              )}
              <td
                colSpan={metaCols.length + dateCols - (phonePortrait && metaCols[0] ? 1 : 0)}
                className="border-t border-gray-300 px-2 py-1.5 text-right uppercase text-[10px] text-gray-500 font-semibold"
                style={stickySpan(CHECK_W + READY_W, frozenEnd - CHECK_W - READY_W, "var(--sticky-head-bg)", 15)}
              >
                Total
              </td>
              {columns.map((c) => (
                <td key={c.key} className="border-t border-gray-300 dark:border-gray-700 px-1 py-1.5 text-center tabular-nums whitespace-nowrap bg-gray-100 dark:bg-gray-800" style={{ minWidth: 48 }}>
                  {TOTALS[c.key] || 0}
                </td>
              ))}
              {showLocation && (
                <td className="border-t border-gray-300 border-l px-2 py-1.5 bg-gray-100 dark:bg-gray-800"></td>
              )}
              <td className="border-t border-gray-300 px-2 py-1.5 text-right tabular-nums bg-gray-200/70"
                  data-testid="grid-grand-total">{GRAND}</td>
              {DEL && DEL_FIELDS.slice(1).map((f) => (
                <td key={f} data-testid={`grid-grand-delivery-${f}`}
                    className={`border-t border-l border-gray-300 px-2 py-1.5 text-right tabular-nums bg-gray-200/70 ${
                      f === "placed" ? "text-emerald-800" : f === "not_delivered" ? "text-red-800" : "text-gray-800"}`}>
                  {delSum(rooms, f)}
                </td>
              ))}
              <PctCell {...pctOf(rooms)} testid="grid-pct-grand" bg="var(--sticky-head-bg)" />
            </tr>
          </tbody>
        </table>
      </div>
      <div className={`px-3 py-1.5 flex items-center gap-3 text-[11px] text-gray-500 border-t border-gray-200 bg-gray-50/40 ${installMode ? "hidden" : "flex"}`}>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm border bg-emerald-50 border-emerald-200"></span>
          ready
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm border bg-blue-50 border-blue-200"></span>
          scheduled
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm border bg-gray-100 border-gray-300"></span>
          completed
        </span>
        <span className="ml-auto">Tip: click a Building or Floor header to collapse/expand. Select whole rooms via the row checkbox.</span>
      </div>

      {/* Full product detail — the same read-only sheet in the RA view and the
          installer view. Product data is edited in the RA grid. */}
      {productInfo && (() => {
        const p = productFields(productInfo);
        const roomsWith = rooms.filter((r) => (qtyMap[r.key] || {})[productInfo.key]).length;
        const rows = [
          ["Part number", p.part],
          ["Code", p.code],
          ["Short code", p.short],
          ["Model", p.model],
          ["Keynote", p.keynote],
          ["Scope", p.scope],
          ["Description", p.desc],
          ["Full FRS text", p.full],
          ["Manufacturer", p.maker],
          ["Color", p.color],
          ["Size", p.size],
          ["Finish", p.finish],
          ["Unit", p.uom],
          ["Total units", String(totalsByCol[productInfo.key] || 0)],
          ["Rooms", `${roomsWith}`],
        ].filter(([, v]) => hasVal(v));
        return (
          <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
               onClick={(e) => { if (e.target === e.currentTarget) setProductInfo(null); }}
               data-testid="product-detail-sheet">
            <button
              type="button"
              aria-label="Close product details"
              onClick={() => setProductInfo(null)}
              data-testid="product-detail-backdrop"
              className="absolute inset-0 bg-black/40"
            />
            <div className="relative w-full sm:w-[420px] max-h-[70vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 shadow-2xl">
              <div className="sticky top-0 flex items-center gap-2 px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-200">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
                       data-testid="product-detail-title">
                    {p.part || p.desc || p.keynote || "Product"}
                  </div>
                  <div className="text-[11px] text-gray-500">{productInfo.scope}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setProductInfo(null)}
                  data-testid="product-detail-close"
                  className="ml-auto h-10 w-10 grid place-items-center rounded-full border border-gray-200 text-gray-600 active:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <dl className="px-4 py-3 space-y-2 text-sm">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex gap-3">
                    <dt className="w-32 shrink-0 text-[11px] uppercase tracking-wide text-gray-400 pt-0.5">{k}</dt>
                    <dd className="flex-1 text-gray-900 dark:text-gray-100 break-words">{v}</dd>
                  </div>
                ))}
              </dl>
              {/* P2-2 — the column colour: Auto reads Color › Finish › Description; a pull wins. */}
              {!installMode && onSetTint && (
                <div className="px-4 pb-3" data-testid="product-tint-picker">
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Column colour</div>
                  <div className="flex flex-wrap gap-1.5">
                    {[["", "Auto"], ["none", "None"]].map(([v, lbl]) => (
                      <button key={lbl} type="button" onClick={() => { onSetTint(productInfo, v); setProductInfo({ ...productInfo, tint: v }); }}
                              data-testid={`product-tint-${lbl.toLowerCase()}`} data-active={(productInfo.tint || "") === v ? "1" : "0"}
                              className={`h-7 rounded-full border px-2 text-[11px] ${(productInfo.tint || "") === v ? "border-gray-900 font-semibold dark:border-gray-100" : "border-gray-300 dark:border-gray-600"} text-gray-700 dark:text-gray-200`}>{lbl}</button>
                    ))}
                    {TINT_WORDS.map(([w, rgb]) => (
                      <button key={w} type="button" title={w} onClick={() => { onSetTint(productInfo, w); setProductInfo({ ...productInfo, tint: w }); }}
                              data-testid={`product-tint-${w.toLowerCase()}`} data-active={productInfo.tint === w ? "1" : "0"}
                              className={`h-7 w-7 rounded-full border-2 ${productInfo.tint === w ? "border-gray-900 dark:border-white" : "border-transparent"}`}
                              style={{ background: rgb }} />
                    ))}
                  </div>
                </div>
              )}
              <div className="px-4 pb-4 flex gap-2">
                {/* Correct the product without leaving the grid. RA only —
                    the installer view reads product data, it never edits it. */}
                {!installMode && onEditProduct && (
                  <button
                    type="button"
                    title="Edit this product's data"
                    onClick={() => {
                      onEditProduct({
                        product_uid: productInfo.uid,
                        scope_type: p.scope,
                        label: [p.part, p.desc].filter(Boolean).join(" — ") || p.short,
                        product_part_number: p.part, product_code: p.code,
                        product_short: p.short, product_model: p.model,
                        product_keynote: p.keynote, product_desc: p.desc,
                        product_description: p.full, manufacturer: p.maker,
                        product_color: p.color, product_size: p.size,
                        product_finish: p.finish, unit_of_measure: p.uom,
                        units: totalsByCol[productInfo.key] || 0, rooms: roomsWith,
                      });
                      setProductInfo(null);
                    }}
                    data-testid="product-detail-edit"
                    className="flex-1 h-11 rounded-xl border border-blue-200 bg-blue-50 text-blue-800 text-sm font-medium hover:bg-blue-100 disabled:opacity-50"
                  >
                    Edit product data
                  </button>
                )}
                {onRemoveProductJob && (
                  <button
                    type="button"
                    title="Remove this product from every room on this scope"
                    onClick={() => {
                      onRemoveProductJob({
                        product_uid: productInfo.uid, scope_type: p.scope,
                        label: [p.part, p.desc].filter(Boolean).join(" — ") || p.short || p.code,
                        units: totalsByCol[productInfo.key] || 0, rooms: roomsWith,
                      });
                      setProductInfo(null);
                    }}
                    data-testid="product-detail-remove-job"
                    className="flex-1 h-11 rounded-xl border border-red-200 bg-red-50 text-red-800 text-sm font-medium hover:bg-red-100"
                  >
                    Remove from the job
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setProductInfo(null)}
                  data-testid="product-detail-done"
                  className="flex-1 h-11 rounded-xl bg-gray-900 text-white text-sm font-medium active:bg-black"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {roomInfo && (() => {
        const r = roomInfo;
        const norm = (v) => String(v || "").replace(/\s+/g, "").toUpperCase();
        const refs = roomRefs(r);
        const facts = [["Building", r.building], ["Floor", r.floor], ["Area", r.area], ["Room type", r.room_type]].filter(([, v]) => hasVal(v));
        return (
          <Dialog open onOpenChange={(v) => { if (!v) setRoomInfo(null); }}>
            <DialogContent className="max-w-sm p-4" data-testid="install-room-info">
              <DialogHeader>
                <DialogTitle className="text-base">
                  {r.room_name || "Room"}{r.room_number ? <span className="ml-2 font-mono text-sm text-gray-600">{r.room_number}</span> : null}
                </DialogTitle>
                <DialogDescription className="text-xs">{facts.map(([k, v]) => `${k} ${v}`).join(" · ") || "Plan references for this room"}</DialogDescription>
              </DialogHeader>
              {/* Mobile reach: the LOCATION column sits far right of a wide matrix, so the room's
                  material locations and their photos are also one tap from the room name. */}
              {(locByRoom[r.key] || new Set()).size > 0 && (
                <div className="mb-2 pb-2 border-b border-gray-100" data-testid="install-room-info-material">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Material on site</div>
                  <LocationCell locations={[...(locByRoom[r.key] || new Set())]}
                                countOf={(label) => photoCounts?.by_room_location?.[`${locKey(label)}|${roomKey3(r)}`] || 0}
                                testid="install-room-info-where"
                                onOpenDetail={onLocationCell ? () => { setRoomInfo(null); onLocationCell(r, itemsByRoom[r.key] || []); } : undefined}
                                onOpenPhotos={onLocationPhotos
                                  ? (label) => onLocationPhotos({ label, room: r,
                                      frs_item_keys: (itemsByRoom[r.key] || []).map((it) => it.frs_item_key).filter(Boolean) })
                                  : undefined} />
                </div>
              )}
              {refs.length === 0 ? (
                <div className="text-sm text-gray-500" data-testid="install-room-info-empty">The FRS carries no drawing references for this room.</div>
              ) : (
                <ul className="divide-y divide-gray-100 text-sm" data-testid="install-room-info-refs">
                  {refs.map(([label, val]) => {
                    const linkable = onOpenPlan && planLinks?.sheets?.has(norm(val));
                    return (
                      <li key={label} className="flex items-center justify-between gap-3 py-2">
                        <span className="text-[11px] uppercase tracking-tight text-gray-500">{label}</span>
                        {linkable ? (
                          <button type="button" className="font-medium text-blue-700 underline decoration-dotted hover:text-blue-900"
                                  onClick={() => { setRoomInfo(null); onOpenPlan({ sheet: val, room: r.room_number }); }}
                                  data-testid={`install-room-info-open-${label.toLowerCase().replace(/\s+/g, "-")}`}>{val}</button>
                        ) : (
                          <span className="font-medium text-gray-900" data-testid={`install-room-info-${label.toLowerCase().replace(/\s+/g, "-")}`}>{val}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {onOpenPlan && planLinks?.rooms?.has(String(r.room_number || "")) && (
                <button type="button" className="mt-1 h-9 w-full rounded-md bg-blue-600 text-sm font-medium text-white"
                        onClick={() => { setRoomInfo(null); onOpenPlan({ room: r.room_number }); }} data-testid="install-room-info-open-plan">
                  Open this room on the plan
                </button>
              )}
              {shopLinks?.[String(r.room_number || "")]?.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="install-room-info-shop">
                  <div className="text-[11px] uppercase tracking-tight text-gray-500">Field shop drawings</div>
                  {shopLinks[String(r.room_number || "")].map((l) => (
                    <button key={l.sheet_id} type="button" onClick={() => { setRoomInfo(null); openDrawing(navigate, { project: projectId, sheetId: l.sheet_id, room: r.room_number, from: linkFrom, scopes: scopeFilter || [] }); }}
                            className="w-full text-left flex items-center gap-2 min-h-11 px-2 rounded-md border hover:bg-gray-50" data-testid={`install-room-info-shop-${l.sheet_id}`}>
                      <PurposeChip purpose={l.purpose} scopes={l.scopes} size="sm" /><span className="text-xs text-gray-600 truncate">{l.sheet_number}</span>
                    </button>
                  ))}
                </div>
              )}
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
}
